import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { Prompt, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  Chip,
  TextField,
  Tooltip,
  Typography,
  CardContent,
  Grid,
  Divider,
  styled,
  Switch,
} from "@material-ui/core";
import Backdrop from "@material-ui/core/Backdrop";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import DeleteIcon from "@material-ui/icons/Delete";
import { useFormik } from "formik";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonButton from "src/components/CommonButton/CommonButton";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import { Autocomplete } from "@material-ui/lab";
import TabHeader from "src/components/TabHeader/TabHeader";
import { NodCommercialRoute } from "../../nod-commercial.routes";
import { columns } from "./components/columns";
import * as Yup from "yup";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import {
  selectLoader,
  selectPaymentTypes,
  selectWeightScanTypes,
  selectPaymentTypeDetail,
  addPaymentAction,
  updatePaymentAction,
  removeGridItem,
  removeAllPayments,
  clearPaymentTypeDetail,
} from "src/app-redux/commercial/commercialSlice";
import { useAppSelector } from "src/app-redux/hooks";
import {
  getPaymentTypesDropdownAction,
  getPaymentTypeDetailsAction,
  postTerminalChargesWholeRecordAction,
} from "src/app-redux/commercial/actions/commercialActions";
import { useStyles } from "./components/style";
import { numberFormatting } from "src/common/utils/helpers";
import Checkbox from "@mui/material/Checkbox";
import TransitionDialog from "src/components/AlertDialog/TransitionDialog";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonLoader from "src/components/Loader/CommonLoader";
import { TerminalDetails } from "./components/TerminalDetails";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import {
  selectAllPartyNameRowsList,
  selectAllTaxTypeRowsList,
  selectAllAgent,
} from "src/app-redux/configuration/configurationSlice";
import {
  getAllPartName,
  getAllTaxTypes,
  getAllAgents,
} from "src/app-redux/configuration/actions/configurationActions";

interface Props {
  pageHeadingOverride?: string;
}

function isBillGroupedInvoiceType(invoiceType: unknown): boolean {
  const t = normalizeInvoiceType(invoiceType);
  return t === "BYD" || t === "Partywise";
}

function shouldSendBillNumberInPayload(invoiceType: unknown): boolean {
  const t = String(invoiceType ?? "").trim();
  return t === "Normal" || isBillGroupedInvoiceType(invoiceType);
}

function billNumberPayloadFields(invoiceType: unknown, billNumber: unknown) {
  if (!shouldSendBillNumberInPayload(invoiceType)) return {};
  const bill = String(billNumber ?? "").trim();
  return bill ? { BillNo: bill } : {};
}

function mapPendingCheckoutIndexRow(row: any) {
  if (!row || typeof row !== "object") return row;
  return {
    ...row,
    indexNumber: row.indexNo ?? row.indexNumber ?? row.index_number,
    billNumber: row.blNo ?? row.billNumber ?? row.billNo,
    containerNumber: row.containerNo ?? row.containerNumber,
    virNo: row.virNo ?? row.virNumber,
    gateInDate: row.gateInTime ?? row.gateInDate,
    destuffingDate: row.destuffDate ?? row.destuffingDate,
  };
}

function extractPendingCheckoutIndexes(results: any): any[] {
  const raw =
    results?.value?.indexes ??
    results?.value?.indexesList ??
    results?.indexes ??
    results?.value?.list ??
    (Array.isArray(results?.value) ? results.value : []);
  return Array.isArray(raw) ? raw.map(mapPendingCheckoutIndexRow) : [];
}

function isDestuffingPending(row: any): boolean {
  return Boolean(row) && row.destuffingDone === false;
}

function shouldRequireDestuffing(invoiceType: unknown): boolean {
  const t = String(invoiceType ?? "").trim();
  return t === "Normal" || t === "BYD";
}

function mergeEditPaymentSources(apiRecord: any, listRecord: any): any {
  const list = listRecord && typeof listRecord === "object" ? listRecord : {};
  const apiRoot = apiRecord && typeof apiRecord === "object" ? apiRecord : {};
  const api =
    apiRoot?.value && typeof apiRoot.value === "object" && !Array.isArray(apiRoot.value)
      ? apiRoot.value
      : apiRoot;

  return {
    ...list,
    ...api,
    paymentId: api?.paymentId ?? api?.id ?? api?.gatePassPaymentId ?? list?.paymentId ?? 0,
    billNumber: api?.billNumber ?? api?.BillNo ?? api?.BillNumber ?? list?.billNumber ?? "",
    containerNumber:
      api?.containerNumber ?? api?.containerNo ?? api?.container ?? list?.containerNo ?? "",
    virNumber: api?.virNo ?? api?.virNumber ?? api?.vir ?? list?.virNo ?? "",
    indexNumber: api?.IndexNo ?? api?.indexNo ?? api?.indexNumber ?? list?.indexNo ?? "",
    invoiceType: normalizeInvoiceType(
      api?.invoiceType ?? api?.InvoiceType ?? list?.invoiceType ?? "Normal"
    ),
    agentNameId: api?.agentId ?? api?.agentNameId ?? list?.agentId ?? 0,
    registrationNumber:
      api?.registrationNumber ?? api?.vehicleNumber ?? list?.registrationNumber ?? "",
    vehicleNumber: api?.vehicleNumber ?? api?.registrationNumber ?? list?.registrationNumber ?? "",
    taxTypeId: api?.taxId ?? api?.taxTypeId ?? list?.taxId ?? "",
    discount: api?.discount ?? list?.discount ?? 0,
    remarks: api?.remarks ?? list?.remarks ?? "",
    currency: api?.currency ?? list?.currency ?? "PKR",
    createdOn:
      api?.createdOn ?? api?.printDate ?? api?.createdDate ?? api?.invoiceDate ?? list?.invoiceDate,
  };
}

function parseEditContainerNumbers(record: any): string[] {
  if (Array.isArray(record?.containerNumbers)) {
    return record.containerNumbers.map((x: any) => String(x || "").trim()).filter(Boolean);
  }
  const raw = record?.containerNumber ?? record?.containerNo ?? record?.container ?? "";
  return String(raw)
    .split(/[,;]+/)
    .map((v) => v.trim())
    .filter(Boolean);
}

function isPaymentDetailRow(row: any): boolean {
  if (!row || typeof row !== "object") return false;
  return (
    row.paymentTypeId != null ||
    row.paymentTypeID != null ||
    row.typeOfPaymentId != null ||
    row.typeOfPaymentID != null ||
    row.charges != null ||
    row.amount != null ||
    row.totalCharges != null ||
    Boolean(String(row.paymentType ?? row.paymentTypeName ?? row.typeOfPayment ?? "").trim())
  );
}

function normalizeEditPaymentDetails(raw: any): any[] {
  if (!raw) return [];

  const directCandidates = [
    raw?.gatePassPaymentDetail,
    raw?.gatePassPaymentDetails,
    raw?.GatePassPaymentDetail,
    raw?.GatePassPaymentDetails,
    raw?.gatePassPaymentDetailList,
    raw?.paymentDetails,
    raw?.paymentDetail,
    raw?.paymentDetailList,
    raw?.paymentDetailsList,
    raw?.details,
    raw?.items,
    raw?.list,
    raw?.value?.gatePassPaymentDetail,
    raw?.value?.gatePassPaymentDetails,
    raw?.value?.gatePassPaymentDetailList,
    raw?.value?.paymentDetails,
    raw?.value?.paymentDetailList,
    raw?.value?.details,
    raw?.value?.list,
  ];

  for (const candidate of directCandidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }

  const seen = new Set<any>();
  const queue: any[] = [raw];
  while (queue.length > 0) {
    const node = queue.shift();
    if (!node || typeof node !== "object" || seen.has(node)) continue;
    seen.add(node);

    if (Array.isArray(node) && node.length > 0 && isPaymentDetailRow(node[0])) {
      return node;
    }

    for (const value of Object.values(node)) {
      if (Array.isArray(value) && value.length > 0 && isPaymentDetailRow(value[0])) {
        return value;
      }
      if (value && typeof value === "object") {
        queue.push(value);
      }
    }
  }

  return [];
}

function mapEditPaymentDetailRow(
  d: any,
  context: { indexNumber: string | number; currency: string },
  catalog: any[] = []
): any {
  const quantity = Number(d?.paymentQuantity ?? d?.quantity ?? 1) || 1;
  let chargesPerUnit = Number(d?.chargesPerUnit ?? d?.chargePerUnit ?? 0);
  if (!chargesPerUnit) {
    const totalCharges = Number(d?.charges ?? d?.amount ?? d?.totalCharges ?? 0);
    chargesPerUnit = quantity > 0 ? totalCharges / quantity : totalCharges;
  }

  const discountPerUnit = Number(d?.discountPerUnit ?? 0);
  const discountTotal = Number(d?.discount ?? 0);
  const discount =
    discountPerUnit || (quantity > 0 && discountTotal ? discountTotal / quantity : discountTotal);

  const indexNo = d?.IndexNo ?? d?.indexNo ?? d?.indexNumber ?? context.indexNumber;
  const apiPaymentTypeName = String(
    d?.paymentType ?? d?.paymentTypeName ?? d?.typeOfPayment ?? ""
  ).trim();
  const resolvedType = resolvePaymentTypeFromCatalog(
    d?.paymentTypeId ?? d?.paymentTypeID ?? d?.typeOfPaymentId ?? d?.typeOfPaymentID,
    apiPaymentTypeName,
    catalog
  );
  const tariffParent = getTariffParentFromCatalog(catalog);
  const isTariffBreakdown =
    Boolean(tariffParent) &&
    Number(resolvedType.paymentTypeId) === Number(tariffParent?.id) &&
    Boolean(apiPaymentTypeName) &&
    apiPaymentTypeName.toLowerCase() !== TARIFF_PAYMENT_NAME.toLowerCase();

  return {
    IndexNo: indexNo,
    currency: d?.currency ?? context.currency ?? "PKR",
    paymentTypeId: isTariffBreakdown ? Number(tariffParent?.id) : resolvedType.paymentTypeId,
    paymentType: isTariffBreakdown ? apiPaymentTypeName : resolvedType.paymentType,
    isFixed: d?.isFixed ?? resolvedType.isFixed ?? 0,
    otherPaymentName: d?.otherPaymentName ?? "",
    paymentQuantity: quantity,
    charges: chargesPerUnit,
    discount,
    discountedCharges: d?.discountedCharges ?? d?.originalCharges ?? chargesPerUnit + discount,
    paymentDetailId: Number(d?.id ?? d?.paymentDetailId ?? 0) || undefined,
    isExisting: true,
    isEdited: false,
    isTariffBreakdown,
    tariffParentPaymentType: isTariffBreakdown ? TARIFF_PAYMENT_NAME : undefined,
  };
}

function resolveBillGroupedGatePassFields(
  billNumber: unknown,
  containerNumbers: unknown,
  containerNumber: unknown,
  virContainerList: unknown
): { gatePassId: number; gatePassIds: string } {
  const bill = String(billNumber ?? "").trim();
  const selectedContainers: string[] = Array.isArray(containerNumbers)
    ? (containerNumbers as any[]).map((v) => String(v || "").trim()).filter(Boolean)
    : [];
  const fromString: string[] = String(containerNumber || "")
    .split(",")
    .map((v) => String(v || "").trim())
    .filter(Boolean);
  const containers: string[] = (selectedContainers.length ? selectedContainers : fromString)
    .map((v) => String(v || "").trim())
    .filter(Boolean);
  const list = Array.isArray(virContainerList) ? virContainerList : [];

  if (containers.length > 0) {
    const gatePassIds = containers
      .map((cn) => {
        const row = list.find((r: any) => {
          const sameContainer = String(r?.containerNumber || "") === cn;
          if (!sameContainer) return false;
          if (!bill) return true;
          return String(r?.billNumber || "") === bill;
        });
        return String(row?.gatePassId ?? "").trim();
      })
      .filter((v) => v && v !== "0");

    return {
      gatePassId: Number(gatePassIds[0]) || 0,
      gatePassIds: gatePassIds.join(","),
    };
  }

  if (bill) {
    const gatePassIds = [
      ...new Set(
        list
          .filter((r: any) => String(r?.billNumber || "") === bill)
          .map((r: any) => String(r?.gatePassId ?? "").trim())
          .filter((v) => v && v !== "0")
      ),
    ];
    if (gatePassIds.length > 0) {
      return {
        gatePassId: Number(gatePassIds[0]) || 0,
        gatePassIds: gatePassIds.join(","),
      };
    }
  }

  return { gatePassId: 0, gatePassIds: "" };
}

const INVOICE_TYPE_OPTIONS = [
  { id: "Normal", name: "CFS" },
  { id: "BYD", name: "BYD" },
  { id: "Partywise", name: "CY" },
];

function normalizeInvoiceType(raw: unknown): string {
  const t = String(raw ?? "").trim();
  if (!t) return "Normal";
  if (t === "BYD" || t === "Partywise" || t === "Normal") return t;
  const upper = t.toUpperCase();
  if (upper === "BYD") return "BYD";
  if (upper === "CY" || upper === "PARTYWISE") return "Partywise";
  if (upper === "CFS" || upper === "NORMAL") return "Normal";
  return t;
}

const BYD_PAYMENT_TYPE_NAMES = new Set(
  ["Container Services Charges", "Port Scanning Charges", "Tracking & RFID Charges"].map((name) =>
    name.toLowerCase()
  )
);

function isBydPaymentTypeName(rawName: unknown): boolean {
  const name = String(rawName ?? "")
    .trim()
    .toLowerCase();
  if (!name) return false;
  if (BYD_PAYMENT_TYPE_NAMES.has(name)) return true;
  if (name.includes("container") && name.includes("service")) return true;
  if (name.includes("port") && name.includes("scan")) return true;
  if (name.includes("tracking") && (name.includes("rfid") || name.includes("rf"))) return true;
  return false;
}

function isPaymentDetailAllowedForInvoiceType(
  paymentTypeName: string,
  invoiceType: string
): boolean {
  const name = paymentTypeName.toLowerCase();
  const t = normalizeInvoiceType(invoiceType);
  if (t === "BYD") {
    if (!name) return false;
    return isBydPaymentTypeName(name);
  }
  if (!name) return true;
  return !isBydPaymentTypeName(name);
}

function resolvePaymentTypeFromCatalog(
  paymentTypeId: unknown,
  paymentTypeName: unknown,
  catalog: any[]
): { paymentTypeId: number; paymentType: string; isFixed: number } {
  const catalogList = Array.isArray(catalog) ? catalog : [];
  const id = Number(paymentTypeId) || 0;
  const name = String(paymentTypeName ?? "").trim();
  if (id > 0) {
    const match = catalogList.find((pt: any) => Number(pt?.id) === id);
    return {
      paymentTypeId: id,
      paymentType: String(match?.name ?? name),
      isFixed: Number(match?.isFixed ?? 0) || 0,
    };
  }
  if (name) {
    const match = catalogList.find(
      (pt: any) =>
        String(pt?.name ?? "")
          .trim()
          .toLowerCase() === name.toLowerCase()
    );
    if (match) {
      return {
        paymentTypeId: Number(match.id) || 0,
        paymentType: String(match.name ?? name),
        isFixed: Number(match.isFixed ?? 0) || 0,
      };
    }
  }
  return { paymentTypeId: 0, paymentType: name, isFixed: 0 };
}

const TARIFF_PAYMENT_NAME = "Tarrif Payment";

function getTariffParentFromCatalog(catalog: any[]) {
  return (Array.isArray(catalog) ? catalog : []).find(
    (pt: any) =>
      String(pt?.name ?? "")
        .trim()
        .toLowerCase() === TARIFF_PAYMENT_NAME.toLowerCase()
  );
}

function buildTariffPaymentBreakdown(
  nominationObj: any,
  indexCbm: number,
  trackingChargesForTariff: number,
  paymentQuantity: number,
  isPartywise: boolean
) {
  const thcMultiplier = indexCbm > 0 ? indexCbm : 1;
  const containerQty = paymentQuantity || 1;
  const lines: { label: string; charges: number; paymentQuantity: number }[] = [];

  const perCbm = Number(nominationObj.perCbm) || 0;
  if (perCbm > 0 && indexCbm > 0) {
    lines.push({
      label: "Per CBM Charges",
      charges: Math.round(perCbm),
      paymentQuantity: isPartywise ? indexCbm * containerQty : indexCbm,
    });
  }

  const terminalHandlingCharges = Number(nominationObj.terminalHandlingCharges) || 0;
  if (terminalHandlingCharges > 0) {
    lines.push({
      label: "Terminal Handling Charges",
      charges: Math.round(terminalHandlingCharges),
      paymentQuantity: isPartywise ? thcMultiplier * containerQty : thcMultiplier,
    });
  }

  const perIndex = Number(nominationObj.perIndex) || 0;
  if (perIndex > 0) {
    lines.push({
      label: "Per Index Charges",
      charges: Math.round(perIndex),
      paymentQuantity: isPartywise ? containerQty : 1,
    });
  }

  const documentationCharges = Number(nominationObj.documentationCharges) || 0;
  if (documentationCharges > 0) {
    lines.push({
      label: "Documentation Charges",
      charges: Math.round(documentationCharges),
      paymentQuantity: isPartywise ? containerQty : 1,
    });
  }

  const fuelCharges = Number(nominationObj.fuelCharges) || 0;
  if (fuelCharges > 0) {
    lines.push({
      label: "Fuel Charges",
      charges: Math.round(fuelCharges),
      paymentQuantity: isPartywise ? containerQty : 1,
    });
  }

  if (trackingChargesForTariff > 0) {
    lines.push({
      label: "Tracking Charges",
      charges: Math.round(trackingChargesForTariff),
      paymentQuantity: isPartywise ? containerQty : 1,
    });
  }

  return lines;
}

function relabelTariffBreakdownDetails(
  rows: any[],
  opts: {
    catalog: any[];
    nominationObj: any;
    indexCbm: number;
    paymentQuantity: number;
    isPartywise: boolean;
    totalIndexes: number;
  }
): any[] {
  const tariffParent = getTariffParentFromCatalog(opts.catalog);
  if (!tariffParent) return rows;

  const tariffParentId = Number(tariffParent.id);
  const genericTariffRows = rows.filter(
    (row) =>
      Number(row?.paymentTypeId) === tariffParentId &&
      String(row?.paymentType ?? "")
        .trim()
        .toLowerCase() === TARIFF_PAYMENT_NAME.toLowerCase()
  );
  if (!genericTariffRows.length) return rows;

  const trackingChargesForTariff =
    Number(opts.nominationObj?.trackingCharges) > 0
      ? opts.isPartywise
        ? Number(opts.nominationObj.trackingCharges) / (opts.paymentQuantity || 1)
        : Number(opts.nominationObj.trackingCharges) / (opts.totalIndexes || 1)
      : 0;

  const breakdownLines = buildTariffPaymentBreakdown(
    opts.nominationObj,
    opts.indexCbm,
    trackingChargesForTariff,
    opts.paymentQuantity,
    opts.isPartywise
  );
  if (!breakdownLines.length) return rows;

  const usedLabels = new Set<string>();
  return rows.map((row) => {
    if (
      Number(row?.paymentTypeId) !== tariffParentId ||
      String(row?.paymentType ?? "")
        .trim()
        .toLowerCase() !== TARIFF_PAYMENT_NAME.toLowerCase()
    ) {
      return row;
    }

    const rowTotal = Math.round((Number(row?.charges) || 0) * (Number(row?.paymentQuantity) || 1));
    const match = breakdownLines.find((line) => {
      if (usedLabels.has(line.label)) return false;
      const lineTotal = Math.round(line.charges * line.paymentQuantity);
      return lineTotal === rowTotal;
    });
    if (!match) return row;

    usedLabels.add(match.label);
    return {
      ...row,
      paymentType: match.label,
      isTariffBreakdown: true,
      tariffParentPaymentType: TARIFF_PAYMENT_NAME,
    };
  });
}

export function TerminalCharges({ pageHeadingOverride }: Props): React.ReactElement {
  const isExportString = localStorage.getItem("isAllowToExportData");
  const isExport = isExportString === "true";
  const classes = useStyles();
  const allTaxTypeRows = useAppSelector(selectAllTaxTypeRowsList);
  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const moduleName = NodCommercialRoute.title;
  const pageHeading =
    pageHeadingOverride ||
    (NodCommercialRoute.subRoutes ? NodCommercialRoute.subRoutes[0].title : "Page Title");
  const [loader2, setLoader2] = useState<any>(false);
  const [isOtherPayment, setIsOtherPayment] = useState<boolean>(false);
  const [hasUserSelectedPaymentType, setHasUserSelectedPaymentType] = useState<boolean>(false);
  const dispatch = useDispatch();
  const notifyDestuffingRequired = () => {
    dispatch(
      addNotificationAction({
        message: "Please do the Destuffing first.",
        options: { variant: "error" },
      })
    );
  };
  const [isOpenDublicateDailog, setIsOpenDublicateDailog] = useState<boolean>(false);
  const [isVehicleAdd, setVehicleAdd] = useState(false);
  const [pendingContainerList, setPendingContainerList] = React.useState<any>([]);
  const [virContainerList, setVirContainerList] = React.useState<any>([]);
  const [containerIndexesByGatePass, setContainerIndexesByGatePass] = React.useState<any[]>([]);
  const [isOpenDeleteDailog, setIsOpenDeleteDailog] = useState<boolean>(false);
  const [deleteGridItemId, setDeleteGridItemId] = useState<any>("");
  const [totalAmount, setTotalAmount] = useState(0);
  const [filterGridRecord, setFilterGridRecord] = useState<any>([]);
  const [apiSubmitRecordData, setApiSubmitRecordData] = useState<any>([]);
  const loading = useAppSelector(selectLoader);
  const paymentTypes: any[] = useAppSelector(selectPaymentTypes);
  const paymentTypeDetail = useAppSelector(selectPaymentTypeDetail);
  const weightScanTypesRecord = useAppSelector(selectWeightScanTypes);
  const [containerDetails, setContainerDetails] = useState<any>({});
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dialogContent, setDialogContent] = useState<any>();
  const [atCommercialStation, setatCommercialStation] = useState<boolean>(false);
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  const [numberOfCbm, setNumberOfCbm] = useState(0);
  const [vehicleRegNumberArr, setvehicleRegNumberArr] = React.useState<any[]>([]);
  const [vehicleNumberLength, setVehicleNumberLength] = React.useState<number>(0);
  const [, setFilteredNominations] = useState<any[]>([]);
  const [nominationDetails, setnominationDetails] = useState<any>({});
  const terminalType = Number(localStorage.getItem("terminalType"));
  const [isFuturePaymentInvoice, setIsFuturePaymentInvoice] = useState<boolean>(false);
  const [futurePaymentInvoiceDate, setFuturePaymentInvoiceDate] = useState<string>("");
  const [futurePaymentVirList, setFuturePaymentVirList] = React.useState<any[]>([]);
  // "As-of" invoice datetime for charge calculations (supports past invoices)
  const [invoiceDateTime, setInvoiceDateTime] = useState<string>("");
  const [, setVehicleContanerNumbers] = React.useState<any>([]);
  const [, setStayDays] = useState(0);
  const [storageDays, setStorageDays] = useState(0);
  const [partywiseContainerMetaCache, setPartywiseContainerMetaCache] = useState<
    Record<string, { gateInDate?: string; containerSize?: number }>
  >({});
  const [, setStorageSlabs] = useState<any[]>([]);
  const [isExportMode, setIsExportMode] = useState<boolean>(false);
  const [, setGdList] = React.useState<any>([]);
  const [, setGdVehiclesList] = React.useState<any>([]);
  const [gdContainerList, setGdContainerList] = React.useState<any>([]);
  // const [scanning, setScanning] = useState<any>();
  // const [weight, setWeight] = useState<any>();
  // const [cargoType, setcargoType] = useState<string>("");
  // const [consignmentType, setconsignmentType] = useState<string>("");
  // const [vehicleType, setvehicleType] = useState<string>("");
  // const [entryDateTime, setentryDateTime] = useState<string>("");
  const [vehicleStatusData, setVehicleStatusData] = useState<any>({});
  const [discountApplied, setDiscountApplied] = useState<boolean>(false);
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [originalCharges, setOriginalCharges] = useState<number>(0);
  const location = useLocation<any>();
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editGatePaymentId, setEditGatePaymentId] = useState<number>(0);
  const [editingGridItemId, setEditingGridItemId] = useState<number | null>(null);
  const [editingGridItemMeta, setEditingGridItemMeta] =
    useState<{
      paymentDetailId?: number;
      isExisting?: boolean;
      isTariffBreakdown?: boolean;
    } | null>(null);
  const skipPaymentTypeAutoChargeRef = useRef(false);
  const tariffRelabelKeyRef = useRef("");
  const [editPrefillSyncTick, setEditPrefillSyncTick] = useState(0);
  const [confirmMode, setConfirmMode] = useState<"save" | "update">("save");
  const invoiceManifestFetchGenRef = useRef(0);
  const editPrefillInFlightRef = useRef(false);
  const editPrefillGenRef = useRef(0);
  const lastEditPrefillKeyRef = useRef("");
  const isOpenTruckPrevRef = useRef<boolean | null>(null);

  const allPartyNameRows = useAppSelector(selectAllPartyNameRowsList);
  const allAgetns = useAppSelector(selectAllAgent);

  const getTodayYmd = (): string => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const getNowYmdHm = (): string => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const effectiveChargesDate = React.useMemo(() => {
    if (terminalType === 4) return new Date();
    if (!isFuturePaymentInvoice) return new Date();
    if (!futurePaymentInvoiceDate) return new Date();
    // End-of-day keeps "selected date" intuitive for day-based slabs.
    const d = new Date(`${futurePaymentInvoiceDate}T23:59:59`);
    return Number.isNaN(d.getTime()) ? new Date() : d;
  }, [futurePaymentInvoiceDate, isFuturePaymentInvoice, terminalType]);

  const effectiveChargesDateForCalculations = React.useMemo(() => {
    // For terminalType 4, this screen doesn't expose invoice datetime selection.
    if (terminalType === 4) return new Date();

    // Future payment invoice uses the dedicated future date.
    if (isFuturePaymentInvoice) return effectiveChargesDate;

    // Normal invoice: if user selected a past Invoice Date & Time, calculate charges as-of that time.
    const d = new Date(invoiceDateTime || "");
    return Number.isNaN(d.getTime()) ? new Date() : d;
  }, [effectiveChargesDate, invoiceDateTime, isFuturePaymentInvoice, terminalType]);

  const parseBackendDateToLocal = (raw: any): Date | null => {
    if (!raw) return null;
    if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
    const s = String(raw).trim();
    if (!s) return null;

    // Supports:
    // - ISO (`2026-04-01T...`)
    // - `YYYY-MM-DD ...`
    // - `DD-MM-YYYY ...` (common in this app's UI)
    // - `/` as separator
    const normalized = s.replace(/\//g, "-");

    const dmy = normalized.match(/^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (dmy) {
      const [, dd, mm, yyyy, hh, min, sec] = dmy;
      const dt = new Date(
        Number(yyyy),
        Number(mm) - 1,
        Number(dd),
        hh ? Number(hh) : 0,
        min ? Number(min) : 0,
        sec ? Number(sec) : 0
      );
      return Number.isNaN(dt.getTime()) ? null : dt;
    }

    const ymd = normalized.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (ymd) {
      const [, yyyy, mm, dd, hh, min, sec] = ymd;
      const dt = new Date(
        Number(yyyy),
        Number(mm) - 1,
        Number(dd),
        hh ? Number(hh) : 0,
        min ? Number(min) : 0,
        sec ? Number(sec) : 0
      );
      return Number.isNaN(dt.getTime()) ? null : dt;
    }

    const fallback = new Date(s);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  };

  /** Calendar day difference (exclusive end): used for grace/validation and index selection flows. */
  const diffCalendarDays = (from: Date, to: Date) => {
    const a = new Date(from);
    const b = new Date(to);
    a.setHours(0, 0, 0, 0);
    b.setHours(0, 0, 0, 0);
    return Math.floor((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
  };

  /**
   * Inclusive day span for loose-cargo "Storage Payment Per Ton/Day" only (terminalType === 4).
   * Same calendar day => 1 day; 1 Apr -> 16 Apr => 16 days.
   * Not used for slab-based "Storage Charges" (5-day grace etc.) — that uses `calculateStoragePayment`.
   */
  const diffCalendarDaysInclusiveStorageTonDay = (from: Date, to: Date) => {
    const diff = diffCalendarDays(from, to);
    return diff >= 0 ? diff + 1 : diff;
  };

  const partywiseContainerCacheKey = (row: any, containerNumber?: string) =>
    `${row?.gatePassId ?? row?.gateEntryId ?? ""}|${row?.indexNumber ?? ""}|${
      containerNumber ?? row?.containerNumber ?? ""
    }`;

  const resolveRowGateInRaw = (row: any, containerType?: string) => {
    const ct = containerType ?? row?.containerType ?? "CY";
    if (ct === "CY") {
      return row?.gateInDate ?? row?.gateEntryTime ?? row?.createdDateTime ?? null;
    }
    return row?.destuffingDate ?? row?.gateInDate ?? row?.createdDateTime ?? null;
  };

  const resolveRowContainerSize = (
    row: any,
    cached?: { gateInDate?: string; containerSize?: number }
  ): number | null => {
    const raw =
      cached?.containerSize ??
      row?.containerSize ??
      row?.container_size ??
      row?.size ??
      row?.containerSz ??
      null;
    const size = Number(raw);
    return size === 20 || size === 40 || size === 45 ? size : null;
  };

  const resolvePartywiseCySlabKey = (containerSize: number | null | undefined) => {
    if (containerSize === 45) return "CY45";
    if (containerSize === 40) return "CY40";
    if (containerSize === 20) return "CY20";
    return null;
  };

  const resolvePartyIdToNumber = (raw: any): number | null => {
    if (raw === "" || raw == null) return null;
    const num = Number(raw);
    if (!Number.isNaN(num) && num > 0) return num;
    const byName = allPartyNameRows.find(
      (p: any) => (p.name && String(p.name).trim()) === String(raw).trim()
    );
    const id = (byName as any)?.id;
    return id != null ? id : null;
  };

  const resolveForwarderDisplayName = (raw: any): string => {
    if (raw === "" || raw == null) return "";
    const rawText = String(raw).trim();
    if (!rawText) return "";
    const partyRows = (allPartyNameRows || []) as any[];

    const byId = partyRows.find((p: any) => String(p?.id) === rawText);
    if (byId?.name) return String(byId.name);

    const byName = partyRows.find((p: any) => String(p?.name || "").trim() === rawText);
    if (byName?.name) return String(byName.name);

    return rawText;
  };

  const hasInvoiceManifestDetails = useMemo(() => {
    const d = containerDetails || {};
    const forwarderId = Number(d.forwarderId);
    if (Number.isFinite(forwarderId) && forwarderId > 0) return true;
    return String(d.nominationTypeName ?? "").trim().length > 0;
  }, [containerDetails]);

  const getNominationForCharges = () => {
    if (
      nominationDetails &&
      (nominationDetails.name ||
        nominationDetails.perCbm != null ||
        nominationDetails.terminalHandlingCharges != null)
    ) {
      return nominationDetails;
    }
    const d = containerDetails || {};
    if (!String(d.nominationTypeName ?? "").trim() && !Number(d.forwarderId)) {
      return {};
    }
    return {
      name: d.nominationTypeName,
      perCbm: Number(d.perCbm) || 0,
      perIndex: Number(d.perIndex) || 0,
      terminalHandlingCharges: Number(d.terminalHandlingCharges) || 0,
      trackingCharges: Number(d.trackingCharges) || 0,
      fuelCharges: Number(d.fuelCharges) || 0,
      documentationCharges: Number(d.documentationCharges) || 0,
    };
  };

  const resolveForwarderIdForPayload = (partyIdRaw?: any): number => {
    const fromManifest = Number(containerDetails?.forwarderId);
    if (Number.isFinite(fromManifest) && fromManifest > 0) return fromManifest;
    const raw = partyIdRaw ?? formik.values.PartyId ?? containerDetails?.forwarder ?? "";
    if (raw === "") return 0;
    const num = Number(raw);
    if (!Number.isNaN(num) && num > 0) return num;
    const byName = allPartyNameRows.find(
      (p: any) => (p.name && String(p.name).trim()) === String(raw).trim()
    );
    const id = (byName as any)?.id;
    return id ?? (typeof raw === "number" ? raw : 0);
  };

  const applyInvoiceManifestSideEffects = (value: any) => {
    const nominationTypeName = String(value?.nominationTypeName ?? "").trim();
    if (nominationTypeName || value?.forwarderId != null) {
      setnominationDetails({
        name: nominationTypeName,
        perCbm: Number(value?.perCbm) || 0,
        perIndex: Number(value?.perIndex) || 0,
        terminalHandlingCharges: Number(value?.terminalHandlingCharges) || 0,
        trackingCharges: Number(value?.trackingCharges) || 0,
        fuelCharges: Number(value?.fuelCharges) || 0,
        documentationCharges: Number(value?.documentationCharges) || 0,
      });
      if (nominationTypeName) {
        formik.setFieldValue("nominationName", nominationTypeName);
      }
    }
    const agentId = Number(value?.agentId);
    if (Number.isFinite(agentId) && agentId > 0) {
      formik.setFieldValue("agentNameId", agentId);
    }
    if (value?.agentChallanNumber != null) {
      formik.setFieldValue("challanNumber", value.agentChallanNumber);
    }
  };

  const getNominationsByParty = async (PartyId: number | string) => {
    const numericId = typeof PartyId === "number" ? PartyId : resolvePartyIdToNumber(PartyId);
    if (numericId == null) {
      setFilteredNominations([]);
      return;
    }
    try {
      setLoader2(true);
      const response = await fetchWrapper.get(
        `get-party-nomination-type-by-id?PartyId=${numericId}`
      );
      if (response.isSuccess) {
        setFilteredNominations(response.value.nominationList || []);
      } else {
        setFilteredNominations([]);
      }
    } catch (err) {
      console.error("Failed to fetch nominations", err);
      setFilteredNominations([]);
    } finally {
      setLoader2(false);
    }
  };

  const getContainersByVir = async (vir: string): Promise<any[]> => {
    try {
      setLoader2(true);
      if (isFuturePaymentInvoice) {
        const results: any = await fetchWrapper.get(
          `get-pending-checkout-indexes?virNo=${encodeURIComponent(vir)}`
        );
        const list = extractPendingCheckoutIndexes(results);
        setVirContainerList(list);
        setLoader2(false);
        return list;
      }

      const results: any = await fetchWrapper.get(
        `get-released-vir-indexes/${encodeURIComponent(vir)}`
      );
      const list = results.isSuccess
        ? results.value?.indexesList ?? results.value?.list ?? results.value ?? []
        : [];
      const normalized = Array.isArray(list) ? list : [];
      setVirContainerList(normalized);
      setLoader2(false);
      return normalized;
    } catch (error: any) {
      setVirContainerList([]);
      setLoader2(false);
      return [];
    }
  };

  const getContainerIndexesByGatePassId = async (gatePassId: string | number) => {
    if (gatePassId === "" || gatePassId == null) {
      setContainerIndexesByGatePass([]);
      return;
    }
    try {
      setLoader2(true);
      const results: any = await fetchWrapper.get(
        `get-all-container-indexes-by-gatepass-id/${gatePassId}`
      );
      if (results?.isSuccess) {
        const list =
          results?.value?.indexesList ??
          results?.value?.containerIndexesList ??
          results?.value?.list ??
          results?.value ??
          [];
        setContainerIndexesByGatePass(Array.isArray(list) ? list : []);
      } else {
        setContainerIndexesByGatePass([]);
      }
    } catch (error) {
      console.error("Failed to fetch container indexes by gate pass id", error);
      setContainerIndexesByGatePass([]);
    } finally {
      setLoader2(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setIsOpenDeleteDailog(false);
  };

  const handleDeleteDialog = () => {
    setIsOpenDeleteDailog(false);
    dispatch(removeGridItem(deleteGridItemId));
  };

  const handleRemoveGridItem = (params: any) => {
    if (params?.isExisting) return;
    setIsOpenDeleteDailog(true);
    setDeleteGridItemId(params.id);
  };

  const handleGridRowClick = (row: any) => {
    if (!isEditMode || !row) return;

    const sourceRow = ((weightScanTypesRecord || []) as any[]).find(
      (item) => Number(item?.id) === Number(row?.id)
    );
    if (!sourceRow) return;

    const paymentTypeName = String(sourceRow?.paymentType ?? "");
    const isOther = paymentTypeName === "Other" || Boolean(sourceRow?.otherPaymentName);
    const chargesPerUnit = Number(sourceRow?.charges) || 0;
    const discountedChargesPerUnit =
      terminalType === 4 && sourceRow?.discountedCharges !== undefined
        ? Number(sourceRow.discountedCharges) || 0
        : chargesPerUnit;

    skipPaymentTypeAutoChargeRef.current = true;
    setHasUserSelectedPaymentType(true);
    setEditingGridItemId(Number(sourceRow.id));
    setEditingGridItemMeta({
      paymentDetailId: sourceRow?.paymentDetailId ? Number(sourceRow.paymentDetailId) : undefined,
      isExisting: Boolean(sourceRow?.isExisting),
      isTariffBreakdown: Boolean(sourceRow?.isTariffBreakdown),
    });
    setIsOtherPayment(isOther);
    formik.setFieldValue("paymentTypeId", Number(sourceRow?.paymentTypeId) || 0);
    formik.setFieldValue("paymentType", paymentTypeName);
    formik.setFieldValue("isFixed", Number(sourceRow?.isFixed) || 0);
    formik.setFieldValue("otherPaymentName", String(sourceRow?.otherPaymentName ?? ""));
    formik.setFieldValue("paymentQuantity", Number(sourceRow?.paymentQuantity) || 1);
    formik.setFieldValue("charges", chargesPerUnit);
    setOriginalCharges(discountedChargesPerUnit);
    setDiscountApplied(false);
    setDiscountPercentage(0);
    dispatch(clearPaymentTypeDetail());
  };
  const getVehicleContainerNumbers = async () => {
    setLoader2(true);
    const results = await fetchWrapper.get("get-all-vehicles-and-containers-for-gate-out");
    setVirContainerList(results.value?.containerList);
    setLoader2(false);

    if (results.isSuccess) {
      setVehicleContanerNumbers(results.value);
    }
    return;
  };
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        // indexNumber: Yup.string().when("isOpenTruck", {
        //   is: false,
        //   then: (schema) => schema.required("This field is required"),
        //   otherwise: (schema) => schema.notRequired(),
        // }),
        virNumber: Yup.string().test(
          "virNumber-required",
          "This field is required",
          function (value) {
            const { isOpenTruck } = this.parent;
            if (isOpenTruck || isExportMode) return true;
            return Boolean(value);
          }
        ),
        billNumber: Yup.string().test(
          "billNumber-required",
          "This field is required",
          function (value) {
            const { invoiceType, isOpenTruck } = this.parent;
            if (isOpenTruck || isExportMode) return true;
            if (!isBillGroupedInvoiceType(invoiceType)) return true;
            if (terminalType === 4) return true; // bill picker is only shown for terminalType !== 4
            return Boolean(value);
          }
        ),
        // GD number input is no longer available in the form; keep it optional to avoid blocking submit
        gdNumber: Yup.string(),
        containerNumber: Yup.string().test(
          "containerNumber-required",
          "This field is required",
          function (value) {
            const { isOpenTruck, invoiceType } = this.parent;
            if (isOpenTruck || invoiceType === "Partywise") return true;
            return Boolean(value);
          }
        ),
        vehicleNumber: Yup.string().test(
          "vehicleNumber-required",
          "This field is required",
          function (value) {
            const { isOpenTruck } = this.parent;
            if (isOpenTruck) return Boolean(value);
            if (isExportMode && !isOpenTruck) return Boolean(value);
            return true;
          }
        ),
        paymentType: Yup.string().required("This field is required"),
        paymentQuantity: Yup.number()
          .required("This field is required")
          .max(10000000000, "Maximum 10 digits are allowed")
          .test(
            "Is positive?",
            "The Quantity must be equal or greater than 0.",
            (value: any) => value > -1
          ),
        discount: Yup.number()
          .max(10000000000, "Maximum 10 digits are allowed")
          .test(
            "Is positive?",
            "The discount must be equal or greater than 0.",
            (value: any) => value > -1
          ),
        charges: Yup.number()
          .required("This field is required")
          .max(1000000000000000, "Maximum 15 digits are allowed")
          .test(
            "Is positive?",
            "The Charges must be equal or greater than 0.",
            (value: any) => value > -1
          ),
        agentNameId: Yup.number()
          .typeError("Agent Name is required")
          .moreThan(0, "Agent Name is required")
          .required("Agent Name is required"),
        nominationName: Yup.mixed().notRequired(),
        remarks: Yup.string().max(3000, "Maximum 3000 characters allowed"),
        printDate:
          terminalType !== 4
            ? Yup.string().required("This field is required")
            : Yup.mixed().notRequired(),
      }),
    [isExportMode, terminalType]
  );

  const formik = useFormik({
    initialValues: {
      invoiceType: "Normal",
      billNumber: "",
      containerNumbers: [] as string[],
      gatePassId: "",
      printDate: getNowYmdHm(),
      containerNumber: "",
      virNumber: "",
      indexNumber: "",
      indexWeight: "",
      vehicleNumber: "",
      currency: "PKR",
      paymentTypeId: 0,
      paymentType: "",
      isFixed: 0,
      otherPaymentName: "",
      paymentQuantity: 1,
      charges: "",
      remarks: "",
      taxTypeId: "",
      discount: 0,
      tax: { taxType: "", taxRate: 0 },
      isOpenTruck: false,
      PartyId: "",
      NominationId: "",
      nominationName: "",
      importer: "",
      gdNumber: "",
      discountApplied: false,
      discountPercentage: 0,
      agentNameId: 0,
      challanNumber: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values: any, actions) => {
      if (extraValidations(values, isOtherPayment) === false) return;
      const temp = values.paymentType;
      if (isOtherPayment) values.paymentType = values.otherPaymentName;

      // Calculate discount and charges after discount for terminalType == 4
      let chargesAfterDiscount = 0;
      let discountAmount = 0;
      const originalChargesAmount =
        originalCharges > 0 ? originalCharges : Number(values.charges) || 0;

      if (terminalType === 4 && discountApplied && discountPercentage > 0 && originalCharges > 0) {
        discountAmount = Math.round((originalCharges * discountPercentage) / 100);
        chargesAfterDiscount = Math.round(originalCharges - discountAmount);
      } else {
        chargesAfterDiscount = Math.round(originalChargesAmount);
      }

      const partywiseContainerCount =
        values.invoiceType === "Partywise"
          ? Array.isArray(values.containerNumbers)
            ? values.containerNumbers.map((x: any) => String(x || "").trim()).filter(Boolean).length
            : 0
          : 0;
      if (values.invoiceType === "Partywise") {
        if (!values.paymentTypeId) {
          dispatch(
            addNotificationAction({
              message: "Please select a payment type so the charge amount can be applied.",
              options: { variant: "error" },
            })
          );
          values.paymentType = temp;
          actions.setSubmitting(false);
          return;
        }
      }

      // Partywise: storage uses latest gate-in days with CY slab per container size; other charges use container count.
      const isPartywiseStorageLine =
        values.invoiceType === "Partywise" &&
        String(values.paymentType || "")
          .trim()
          .toLowerCase() === "storage charges";
      const resolvedPaymentQuantity = (() => {
        if (values.invoiceType !== "Partywise") return values.paymentQuantity;
        if (isPartywiseStorageLine) return 1;
        return partywiseContainerCount;
      })();

      const obj = {
        IndexNo: values.indexNumber,
        ...billNumberPayloadFields(values.invoiceType, values.billNumber),
        currency: values.currency,
        paymentTypeId: values.paymentTypeId,
        paymentType: values.paymentType,
        isFixed: values.isFixed,
        otherPaymentName: values.otherPaymentName,
        paymentQuantity: resolvedPaymentQuantity,
        charges:
          terminalType === 4 && discountApplied && originalCharges > 0
            ? chargesAfterDiscount
            : Math.round(originalChargesAmount),
        discount:
          terminalType === 4 && discountApplied && originalCharges > 0
            ? Math.round(discountAmount)
            : 0,
        discountedCharges:
          terminalType === 4 && discountApplied && originalCharges > 0
            ? Math.round(originalCharges)
            : Math.round(originalChargesAmount),
      };

      if (isEditMode && editingGridItemId) {
        const res: any = await dispatch(
          updatePaymentAction({
            id: editingGridItemId,
            ...obj,
            isExisting: Boolean(editingGridItemMeta?.isExisting),
            paymentDetailId: editingGridItemMeta?.paymentDetailId,
            isEdited: true,
            isTariffBreakdown: Boolean(editingGridItemMeta?.isTariffBreakdown),
            tariffParentPaymentType: editingGridItemMeta?.isTariffBreakdown
              ? TARIFF_PAYMENT_NAME
              : undefined,
          })
        );
        setVehicleAdd(true);
        if (res.payload) {
          values.paymentType = temp;
          actions.setSubmitting(false);
          setIsDirty(false);
          setEditingGridItemId(null);
          setEditingGridItemMeta(null);
          resetFilterForm();
          setOriginalCharges(0);
          return;
        }
      }

      const res: any = await dispatch(addPaymentAction(obj));
      setVehicleAdd(true);
      if (res.payload) {
        values.paymentType = temp;
        actions.setSubmitting(false);
        setIsDirty(false);
        resetFilterForm();
        setOriginalCharges(0);
        return;
      }
    },
  });

  // Keep calculation datetime synced with the Invoice Date & Time field.
  useEffect(() => {
    if (terminalType !== 4) {
      setInvoiceDateTime(formik.values.printDate);
    }
  }, [formik.values.printDate, terminalType]);

  const isBydInvoice = formik.values.invoiceType === "BYD";
  const isPartywiseInvoice = formik.values.invoiceType === "Partywise";
  const isBillGroupedInvoice = isBillGroupedInvoiceType(formik.values.invoiceType);

  const paymentTypesList = useMemo(() => {
    const list = [
      ...paymentTypes.filter((item: any) => item.isFixed == 1),
      ...paymentTypes.filter((item: any) => item.isFixed == 0),
    ];
    return list.concat({
      amount: 0,
      currency: "PKR",
      id: -1,
      isActive: true,
      isFixed: 0,
      name: "Other",
    });
  }, [paymentTypes]);

  const partywiseStorageMetrics = useMemo(() => {
    if (!isPartywiseInvoice) {
      return {
        containerCount: 0,
        baseStorageDays: 0,
        totalStorageDays: 0,
        latestGateInDate: null as Date | null,
        containers: [] as Array<{ containerNumber: string; containerSize: number | null }>,
        sizeBreakdownLabel: "",
      };
    }

    const bill = String(formik.values.billNumber || "");
    const selected = Array.isArray(formik.values.containerNumbers)
      ? formik.values.containerNumbers.map((x: any) => String(x || "").trim()).filter(Boolean)
      : [];
    if (!bill || !selected.length) {
      return {
        containerCount: 0,
        baseStorageDays: 0,
        totalStorageDays: 0,
        latestGateInDate: null as Date | null,
        containers: [] as Array<{ containerNumber: string; containerSize: number | null }>,
        sizeBreakdownLabel: "",
      };
    }

    const asOfDate = effectiveChargesDateForCalculations ?? new Date();
    let latestGateIn: Date | null = null;
    const containers: Array<{ containerNumber: string; containerSize: number | null }> = [];

    for (const cn of selected) {
      const row = (virContainerList || []).find(
        (r: any) => String(r?.billNumber || "") === bill && String(r?.containerNumber || "") === cn
      );
      if (!row) {
        containers.push({ containerNumber: cn, containerSize: null });
        continue;
      }

      const cacheKey = partywiseContainerCacheKey(row, cn);
      const cachedMeta = partywiseContainerMetaCache[cacheKey];
      const raw = resolveRowGateInRaw(row, "CY") ?? cachedMeta?.gateInDate ?? null;
      const gateInDate = parseBackendDateToLocal(raw);
      if (gateInDate && (!latestGateIn || gateInDate.getTime() > latestGateIn.getTime())) {
        latestGateIn = gateInDate;
      }

      containers.push({
        containerNumber: cn,
        containerSize: resolveRowContainerSize(row, cachedMeta),
      });
    }

    if (!latestGateIn && containerDetails && Object.keys(containerDetails).length > 0) {
      const fallbackRaw = resolveRowGateInRaw(containerDetails, "CY");
      latestGateIn = parseBackendDateToLocal(fallbackRaw);
    }

    const baseStorageDays = latestGateIn
      ? Math.max(0, diffCalendarDays(latestGateIn, asOfDate))
      : 0;
    const containerCount = selected.length;

    const sizeCounts = new Map<number, number>();
    for (const container of containers) {
      if (!container.containerSize) continue;
      sizeCounts.set(container.containerSize, (sizeCounts.get(container.containerSize) || 0) + 1);
    }
    const sizeBreakdownLabel = Array.from(sizeCounts.entries())
      .sort(([a], [b]) => a - b)
      .map(([size, count]) => `${size}x${count}`)
      .join(", ");

    return {
      containerCount,
      baseStorageDays,
      totalStorageDays: baseStorageDays * containerCount,
      latestGateInDate: latestGateIn,
      containers,
      sizeBreakdownLabel,
    };
  }, [
    isPartywiseInvoice,
    formik.values.billNumber,
    formik.values.containerNumbers,
    virContainerList,
    partywiseContainerMetaCache,
    containerDetails,
    effectiveChargesDateForCalculations,
  ]);

  useEffect(() => {
    if (!isPartywiseInvoice) return;

    const bill = String(formik.values.billNumber || "");
    const selected = Array.isArray(formik.values.containerNumbers)
      ? formik.values.containerNumbers.map((x: any) => String(x || "").trim()).filter(Boolean)
      : [];
    if (!bill || !selected.length) return;

    const missingRows: any[] = [];
    for (const cn of selected) {
      const row = (virContainerList || []).find(
        (r: any) => String(r?.billNumber || "") === bill && String(r?.containerNumber || "") === cn
      );
      if (!row) continue;

      const cacheKey = partywiseContainerCacheKey(row, cn);
      const cachedMeta = partywiseContainerMetaCache[cacheKey];
      const inlineGateIn = resolveRowGateInRaw(row, "CY");
      const inlineSize = resolveRowContainerSize(row);
      const needsGateIn = !inlineGateIn && !cachedMeta?.gateInDate;
      const needsSize = !inlineSize && !cachedMeta?.containerSize;
      if (needsGateIn || needsSize) {
        missingRows.push({ row, cn, cacheKey });
      }
    }

    if (!missingRows.length) return;

    let cancelled = false;
    (async () => {
      const updates: Record<string, { gateInDate?: string; containerSize?: number }> = {};
      await Promise.all(
        missingRows.map(async ({ row, cacheKey }) => {
          const gatePassId = row?.gatePassId ?? row?.gateEntryId;
          const indexNumber = row?.indexNumber;
          if (!gatePassId || indexNumber == null || indexNumber === "") return;
          try {
            const results = await fetchWrapper.get(
              `get-container-detail/${gatePassId}/index_no/${indexNumber}`
            );
            if (results.isSuccess) {
              const gateInRaw = resolveRowGateInRaw(results.value, "CY");
              const containerSize = resolveRowContainerSize(results.value);
              updates[cacheKey] = {
                ...(gateInRaw ? { gateInDate: String(gateInRaw) } : {}),
                ...(containerSize ? { containerSize } : {}),
              };
            }
          } catch {
            // ignore per-container fetch failures
          }
        })
      );
      if (!cancelled && Object.keys(updates).length) {
        setPartywiseContainerMetaCache((prev) => {
          const next = { ...prev };
          for (const [key, value] of Object.entries(updates)) {
            next[key] = { ...prev[key], ...value };
          }
          return next;
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isPartywiseInvoice,
    formik.values.billNumber,
    formik.values.containerNumbers,
    virContainerList,
    partywiseContainerMetaCache,
  ]);

  useEffect(() => {
    if (!isPartywiseInvoice) return;
    const n = Array.isArray(formik.values.containerNumbers)
      ? formik.values.containerNumbers.map((x: any) => String(x || "").trim()).filter(Boolean)
          .length
      : 0;
    if (Number(formik.values.paymentQuantity) !== n) {
      formik.setFieldValue("paymentQuantity", n);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPartywiseInvoice, formik.values.containerNumbers]);

  useEffect(() => {
    // BYD defaults (only applies to terminalType !== 4 import flow)
    if (isEditMode) return;
    if (terminalType === 4) return;
    if (isExportMode) return;
    if (!isBydInvoice) return;

    const partyRows = (allPartyNameRows || []) as any[];
    const bydParty = partyRows.find(
      (p: any) =>
        String(p?.name || "")
          .trim()
          .toUpperCase() === "BYD"
    );
    const bydPartyId = bydParty?.id;

    if (bydPartyId) {
      if (formik.values.PartyId !== bydPartyId) {
        formik.setFieldValue("PartyId", bydPartyId);
      }
      getNominationsByParty(bydPartyId);
    } else {
      if (formik.values.PartyId !== "BYD") {
        formik.setFieldValue("PartyId", "BYD");
      }
      setFilteredNominations([]);
    }

    if (formik.values.nominationName) {
      formik.setFieldValue("nominationName", "");
    }
    setnominationDetails({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBydInvoice, isExportMode, terminalType, allPartyNameRows]);

  useEffect(() => {
    // Default Tax Type (%) to "Sale tax" (terminalType !== 4 only)
    if (terminalType === 4) return;
    if (!Array.isArray(allTaxTypeRows) || allTaxTypeRows.length === 0) return;
    if (formik.values.taxTypeId !== "" && formik.values.taxTypeId != null) return;

    const saleTaxRow: any =
      allTaxTypeRows.find(
        (t: any) =>
          String(t?.taxType || "")
            .trim()
            .toLowerCase() === "sale tax"
      ) || null;
    if (!saleTaxRow) return;

    formik.setFieldValue("taxTypeId", saleTaxRow.id);
    formik.setFieldValue("tax", saleTaxRow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTaxTypeRows, terminalType, formik.values.taxTypeId]);

  const billNumberOptions = useMemo(() => {
    if (!Array.isArray(virContainerList)) return [];
    const seen = new Set<string>();
    const out: any[] = [];
    for (const row of virContainerList) {
      const bill = row?.billNumber;
      if (!bill) continue;
      const key = String(bill);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ billNumber: key, gatePassId: row?.gatePassId });
    }
    return out;
  }, [virContainerList]);

  const containerOptionsForSelectedBill = useMemo(() => {
    if (!isBillGroupedInvoice) return [];
    const bill = String(formik.values.billNumber || "");
    if (!bill) return [];
    const seen = new Set<string>();
    const out: any[] = [];
    for (const row of virContainerList || []) {
      if (String(row?.billNumber || "") !== bill) continue;
      const cn = row?.containerNumber;
      if (!cn) continue;
      const key = String(cn);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ containerNumber: key });
    }
    return out;
  }, [formik.values.billNumber, isBillGroupedInvoice, virContainerList]);

  const renderBydContainerNumberMultiSelect = () => {
    // only for terminalType !== 4 BYD flow
    const selectAllOption = { containerNumber: "__ALL__" };
    const options = [selectAllOption, ...containerOptionsForSelectedBill];
    const selectedKeys = Array.isArray(formik.values.containerNumbers)
      ? formik.values.containerNumbers
      : [];
    const selectedOptions = containerOptionsForSelectedBill.filter((o: any) =>
      selectedKeys.includes(String(o.containerNumber))
    );

    return (
      <Grid item xs={12} md={6}>
        <Box>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={options}
            value={selectedOptions}
            getOptionLabel={(option: any) =>
              option?.containerNumber === "__ALL__"
                ? "Select All"
                : option?.containerNumber?.toString() || ""
            }
            renderOption={(option: any, { selected }: any) => {
              const isAll = option?.containerNumber === "__ALL__";
              const allSelected =
                containerOptionsForSelectedBill.length > 0 &&
                selectedOptions.length === containerOptionsForSelectedBill.length;
              return (
                <>
                  <Checkbox checked={isAll ? allSelected : selected} />
                  {isAll ? "Select All" : option?.containerNumber}
                </>
              );
            }}
            renderTags={(value: any[], getTagProps: any) => {
              const normalized = (value || [])
                .map((v: any) => String(v?.containerNumber || v || "").trim())
                .filter(Boolean);
              if (!normalized.length) return null;

              const first = normalized[0];
              const remaining = normalized.slice(1);
              const remainingCount = remaining.length;

              return (
                <>
                  <Chip
                    size="small"
                    label={first}
                    {...getTagProps({ index: 0 })}
                    style={{ maxWidth: 180 }}
                  />
                  {remainingCount > 0 && (
                    <Tooltip
                      placement="top"
                      title={
                        <div
                          style={{
                            whiteSpace: "pre-line",
                            fontSize: 14,
                            lineHeight: 1.6,
                            padding: "8px 10px",
                            minWidth: 220,
                            maxWidth: 360,
                            maxHeight: 240,
                            overflowY: "auto",
                          }}
                        >
                          {remaining.join("\n")}
                        </div>
                      }
                    >
                      <Chip
                        size="small"
                        label={`+${remainingCount} more`}
                        style={{ marginLeft: 6 }}
                      />
                    </Tooltip>
                  )}
                </>
              );
            }}
            onChange={(_e: any, newValue: any[]) => {
              const picked = Array.isArray(newValue) ? newValue : [];
              const pickedHasAll = picked.some((x: any) => x?.containerNumber === "__ALL__");
              const allKeys = containerOptionsForSelectedBill.map((x: any) =>
                String(x.containerNumber)
              );

              let nextKeys: string[] = [];
              if (pickedHasAll) {
                const allSelected =
                  containerOptionsForSelectedBill.length > 0 &&
                  selectedOptions.length === containerOptionsForSelectedBill.length;
                nextKeys = allSelected ? [] : allKeys;
              } else {
                nextKeys = picked
                  .map((x: any) => String(x?.containerNumber || ""))
                  .filter((x: string) => x && x !== "__ALL__");
              }

              const bill = String(formik.values.billNumber || "");
              if (shouldRequireDestuffing(formik.values.invoiceType)) {
                const hasPendingDestuffing = nextKeys.some((cn) => {
                  const row = (virContainerList || []).find(
                    (r: any) =>
                      String(r?.billNumber || "") === bill &&
                      String(r?.containerNumber || "") === String(cn)
                  );
                  return isDestuffingPending(row);
                });
                if (hasPendingDestuffing) {
                  notifyDestuffingRequired();
                  return;
                }
              }

              formik.setFieldValue("containerNumbers", nextKeys);
              formik.setFieldValue("containerNumber", nextKeys.join(","));

              // Vehicle Number: show unique vehicle(s) for selected containers (comma-separated)
              if (!nextKeys.length) {
                formik.setFieldValue("vehicleNumber", "");
              } else {
                const bill = String(formik.values.billNumber || "");
                const vehicles = new Set<string>();
                for (const cn of nextKeys) {
                  const rows = (virContainerList || []).filter(
                    (r: any) =>
                      String(r?.billNumber || "") === bill &&
                      String(r?.containerNumber || "") === String(cn)
                  );
                  for (const r of rows) {
                    const v =
                      r?.vehicleRegNumber ??
                      r?.vehicleNumber ??
                      r?.vehicleNo ??
                      r?.registrationNumber ??
                      "";
                    if (v) vehicles.add(String(v));
                  }
                }
                if (vehicles.size) {
                  formik.setFieldValue("vehicleNumber", Array.from(vehicles).join(","));
                }
              }

              // best-effort: set underlying gatePass/index based on first selected container
              const firstCn = nextKeys[0];
              if (!firstCn) {
                formik.setFieldValue("indexNumber", "");
                formik.setFieldValue("gatePassId", "");
                setContainerDetails([]);
                setPartywiseContainerMetaCache({});
                return;
              }

              const firstRow = (virContainerList || []).find(
                (r: any) =>
                  String(r?.billNumber || "") === String(formik.values.billNumber || "") &&
                  String(r?.containerNumber || "") === String(firstCn)
              );
              if (firstRow) {
                formik.setFieldValue("indexNumber", firstRow.indexNumber);
                formik.setFieldValue("gatePassId", firstRow.gatePassId);
                getContailerDetails(firstRow.indexNumber, firstRow.gatePassId, firstRow);
              }
            }}
            renderInput={(params: any) => (
              <TextField
                {...params}
                className="customTextField"
                label="Container Number"
                placeholder="Select Container Number"
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                error={formik.touched.containerNumber && Boolean(formik.errors.containerNumber)}
                helperText={formik.touched.containerNumber && formik.errors.containerNumber}
              />
            )}
          />
        </Box>
      </Grid>
    );
  };

  const extraValidations = (values: any, isOtherPayment: boolean) => {
    if (isOtherPayment) {
      if (values.otherPaymentName === "") {
        dispatch(
          addNotificationAction({
            message: "Other Payment Name is Required",
            options: { variant: "error" },
          })
        );
        return false;
      }
    }
    return true;
  };

  const resetWholePage = (
    options: boolean | { retainToggle?: boolean; invoiceType?: string } = false
  ) => {
    const retainToggle = typeof options === "boolean" ? options : Boolean(options?.retainToggle);
    const nextInvoiceType =
      typeof options === "object" && options?.invoiceType != null
        ? options.invoiceType
        : formik.initialValues.invoiceType;
    const currentIsOpenTruck = formik.values.isOpenTruck;

    formik.resetForm({
      values: {
        ...formik.initialValues,
        invoiceType: nextInvoiceType,
        isOpenTruck: retainToggle ? currentIsOpenTruck : formik.initialValues.isOpenTruck,
      },
    });

    setVehicleAdd(false);
    setatCommercialStation(false);
    setEditingGridItemId(null);
    setEditingGridItemMeta(null);
    tariffRelabelKeyRef.current = "";
    dispatch(removeAllPayments());
    dispatch(clearPaymentTypeDetail());
    setApiSubmitRecordData([]);
    setFilterGridRecord([]);
    setTotalAmount(0);
    setVirContainerList([]);
    setContainerDetails([]);
    setnominationDetails({});
    setFilteredNominations([]);
    setNumberOfCbm(0);
    setStayDays(0);
    setVehicleStatusData({});
    setDiscountApplied(false);
    setDiscountPercentage(0);
    setOriginalCharges(0);
    setIsDirty(false);
    setIsOtherPayment(false);
    setPartywiseContainerMetaCache({});
    setHasUserSelectedPaymentType(false);
    setContainerIndexesByGatePass([]);
    setStorageDays(0);
    if (isExportMode) {
      setGdVehiclesList([]);
      setGdContainerList([]);
    }
  };

  /* eslint-disable react-hooks/rules-of-hooks */
  useEffect(() => {
    if (isEditMode) return;
    if (Number(location?.state?.paymentId ?? 0) > 0 && location?.state?.editPayment) return;
    if (isOpenTruckPrevRef.current === formik.values.isOpenTruck) return;
    isOpenTruckPrevRef.current = formik.values.isOpenTruck;
    resetWholePage(true); // Retain toggle value
  }, [
    formik.values.isOpenTruck,
    isEditMode,
    location?.state?.paymentId,
    location?.state?.editPayment,
  ]);
  useEffect(() => {
    dispatch(getAllPartName(true));
    dispatch(getAllAgents(true));
    formik.setFieldValue("charges", "");
  }, []);

  const resetFilterForm = () => {
    setEditingGridItemId(null);
    setEditingGridItemMeta(null);
    formik.resetForm();
    formik.setValues({
      ...formik.initialValues,
      invoiceType: formik.values.invoiceType,
      billNumber: formik.values.billNumber,
      remarks: formik.values.remarks,
      gatePassId: formik.values.gatePassId,
      printDate: formik.values.printDate,
      containerNumbers: formik.values.containerNumbers,
      virNumber: formik.values.virNumber,
      containerNumber: formik.values.containerNumber,
      indexNumber: formik.values.indexNumber,
      vehicleNumber: formik.values.vehicleNumber,
      taxTypeId: formik.values.taxTypeId,
      discount: formik.values.discount,
      tax: formik.values.tax,
      charges: formik.values.charges,
      gdNumber: formik.values.gdNumber,
      agentNameId: formik.values.agentNameId,
      challanNumber: formik.values.challanNumber,
      PartyId: formik.values.PartyId,
      NominationId: formik.values.NominationId,
      nominationName: formik.values.nominationName,
    });
    setIsDirty(false);
    setIsOtherPayment(false);
    setDiscountApplied(false);
    setDiscountPercentage(0);
    setOriginalCharges(0);
    setOriginalCharges(0);
    if (isExportMode) {
      setGdVehiclesList([]);
      setGdContainerList([]);
    }
  };

  const prefillFromEditPayment = async (editPayment: any) => {
    if (!editPayment) return;

    const prefillGen = ++editPrefillGenRef.current;
    invoiceManifestFetchGenRef.current += 1;
    editPrefillInFlightRef.current = true;
    tariffRelabelKeyRef.current = "";

    dispatch(removeAllPayments());
    dispatch(clearPaymentTypeDetail());
    setFilterGridRecord([]);
    setApiSubmitRecordData([]);
    setTotalAmount(0);

    try {
      const merged = mergeEditPaymentSources(editPayment, location?.state?.listPayment);
      if (prefillGen !== editPrefillGenRef.current) return;

      const invoiceType = normalizeInvoiceType(
        merged?.invoiceType ?? merged?.InvoiceType ?? formik.values.invoiceType ?? "Normal"
      );

      setIsEditMode(true);
      formik.setFieldValue("invoiceType", invoiceType);
      setEditGatePaymentId(
        Number(location?.state?.paymentId ?? merged?.paymentId ?? merged?.id ?? 0) || 0
      );

      const vehicleNumber =
        merged?.vehicleNumber ??
        merged?.registrationNumber ??
        merged?.vehicleNo ??
        merged?.regNo ??
        "";
      const gatePassId = merged?.gatePassId ?? merged?.gatePassID ?? merged?.gatePass ?? "";
      const containerNumber =
        merged?.containerNumber ?? merged?.containerNo ?? merged?.container ?? "";
      const containerNumbers = parseEditContainerNumbers(merged);
      const virNumber = String(merged?.virNumber ?? merged?.virNo ?? merged?.vir ?? "").trim();
      const indexNumber = merged?.IndexNo ?? merged?.indexNo ?? merged?.indexNumber ?? "";
      const billNumber =
        merged?.billNumber ?? merged?.BillNo ?? merged?.BillNumber ?? merged?.bill_no ?? "";
      const currency = merged?.currency ?? formik.values.currency ?? "PKR";
      const remarks = merged?.remarks ?? "";
      const taxTypeId = merged?.taxId ?? merged?.taxTypeId ?? "";
      const agentNameId = merged?.agentId ?? merged?.agentNameId ?? 0;
      const partyId = merged?.partyId ?? merged?.PartyId ?? merged?.forwarderId ?? "";

      const createdOnRaw =
        merged?.createdOn ?? merged?.printDate ?? merged?.createdDate ?? merged?.invoiceDate ?? "";
      const createdOnDt = parseBackendDateToLocal(createdOnRaw);
      const createdOn =
        createdOnDt != null
          ? `${createdOnDt.getFullYear()}-${String(createdOnDt.getMonth() + 1).padStart(
              2,
              "0"
            )}-${String(createdOnDt.getDate()).padStart(2, "0")}T${String(
              createdOnDt.getHours()
            ).padStart(2, "0")}:${String(createdOnDt.getMinutes()).padStart(2, "0")}`
          : formik.values.printDate;

      setatCommercialStation(Boolean(merged?.atCommercialStation));

      let virIndexes: any[] = [];
      if (virNumber) {
        virIndexes = await getContainersByVir(virNumber);
      }

      const resolvedBillNumber =
        String(billNumber ?? "").trim() ||
        String(
          virIndexes.find((r: any) => String(r?.indexNumber ?? "") === String(indexNumber))
            ?.billNumber ?? ""
        ).trim();

      let resolvedContainerNumbers = [...containerNumbers];
      if (!resolvedContainerNumbers.length && resolvedBillNumber && virIndexes.length) {
        resolvedContainerNumbers = [
          ...new Set(
            virIndexes
              .filter((r: any) => String(r?.billNumber ?? "") === resolvedBillNumber)
              .map((r: any) => String(r?.containerNumber ?? "").trim())
              .filter(Boolean)
          ),
        ];
      }

      let resolvedIndexNumber = String(indexNumber ?? "").trim();
      let resolvedGatePassId = String(gatePassId ?? "").trim();
      if (!resolvedIndexNumber && resolvedBillNumber && virIndexes.length) {
        const indexRow =
          virIndexes.find((r: any) => String(r?.billNumber ?? "") === resolvedBillNumber) ??
          virIndexes[0];
        resolvedIndexNumber = String(
          indexRow?.indexNumber ?? indexRow?.indexNo ?? indexRow?.index ?? ""
        ).trim();
        if (!resolvedGatePassId) {
          resolvedGatePassId = String(indexRow?.gatePassId ?? indexRow?.gateEntryId ?? "").trim();
        }
      }

      const taxRow = (allTaxTypeRows as any[]).find(
        (t: any) => String(t?.id ?? "") === String(taxTypeId ?? "")
      );

      formik.setValues({
        ...formik.values,
        vehicleNumber: String(vehicleNumber ?? ""),
        gatePassId: resolvedGatePassId || String(gatePassId ?? ""),
        containerNumber: resolvedContainerNumbers.length
          ? resolvedContainerNumbers.join(",")
          : String(containerNumber ?? ""),
        containerNumbers: resolvedContainerNumbers,
        virNumber: String(virNumber ?? ""),
        indexNumber: resolvedIndexNumber || String(indexNumber ?? ""),
        invoiceType,
        billNumber: resolvedBillNumber,
        currency: String(currency ?? "PKR"),
        remarks: String(remarks ?? ""),
        taxTypeId: taxTypeId === 0 ? "" : String(taxTypeId ?? ""),
        discount: Number(merged?.discount ?? 0) || 0,
        agentNameId: Number(agentNameId ?? 0) || 0,
        PartyId: partyId === 0 ? "" : String(partyId ?? ""),
        printDate: terminalType !== 4 ? String(createdOn) : formik.values.printDate,
        paymentTypeId: 0,
        paymentType: "",
        charges: "",
        paymentQuantity: 1,
        tax: taxRow ?? formik.values.tax,
      });

      if (terminalType !== 4 && createdOn) {
        setInvoiceDateTime(String(createdOn));
      }

      let paymentTypeCatalog = Array.isArray(paymentTypes) ? paymentTypes : [];
      if (!paymentTypeCatalog.length) {
        const typesResult: any = await dispatch(getPaymentTypesDropdownAction());
        paymentTypeCatalog = Array.isArray(typesResult?.payload) ? typesResult.payload : [];
      }
      if (prefillGen !== editPrefillGenRef.current) return;

      let details: any[] = [];
      const paymentId = Number(location?.state?.paymentId ?? merged?.paymentId ?? 0);
      if (paymentId) {
        const detailEndpoints = [
          `get-payment-details-by-payment-id/${paymentId}`,
          `get-gate-pass-payment-detail-by-payment-id/${paymentId}`,
          `get-all-payment-detail-by-payment-id/${paymentId}`,
        ];
        for (const endpoint of detailEndpoints) {
          if (prefillGen !== editPrefillGenRef.current) break;
          try {
            const extraRes: any = await fetchWrapper.get(endpoint);
            const extraDetails = normalizeEditPaymentDetails(extraRes?.value ?? extraRes);
            if (extraDetails.length) {
              details = extraDetails;
              break;
            }
          } catch {
            // try next endpoint shape
          }
        }
      }
      if (!details.length) {
        details = normalizeEditPaymentDetails(merged);
      }

      if (prefillGen !== editPrefillGenRef.current) return;

      dispatch(removeAllPayments());
      details.forEach((d: any) => {
        if (prefillGen !== editPrefillGenRef.current) return;
        dispatch(
          addPaymentAction(
            mapEditPaymentDetailRow(
              d,
              { indexNumber: resolvedIndexNumber || indexNumber, currency },
              paymentTypeCatalog
            )
          )
        );
      });

      if (prefillGen !== editPrefillGenRef.current) return;

      if (virNumber && resolvedBillNumber && (resolvedIndexNumber || indexNumber)) {
        try {
          await getInvoiceManifestDetails(
            virNumber,
            resolvedBillNumber,
            resolvedIndexNumber || indexNumber,
            resolvedGatePassId || gatePassId
          );
        } catch {
          // manifest details are optional for edit prefill
        }
      } else if ((resolvedIndexNumber || indexNumber) && (resolvedGatePassId || gatePassId)) {
        try {
          const row = virIndexes.find(
            (r: any) =>
              String(r?.indexNumber ?? "") === String(resolvedIndexNumber || indexNumber) &&
              String(r?.gatePassId ?? "") === String(resolvedGatePassId || gatePassId)
          );
          await getContailerDetails(
            resolvedIndexNumber || indexNumber,
            resolvedGatePassId || gatePassId,
            row
          );
        } catch {
          // container details are optional for edit prefill
        }
      }

      editPrefillInFlightRef.current = false;
      setEditPrefillSyncTick((tick) => tick + 1);
    } catch (error) {
      console.error("Failed to prefill edit payment", error);
      editPrefillInFlightRef.current = false;
      setEditPrefillSyncTick((tick) => tick + 1);
    }
  };

  useEffect(() => {
    const editPayment = location?.state?.editPayment;
    const paymentId = Number(location?.state?.paymentId ?? 0) || 0;
    const prefillKey = `${paymentId}:${location?.state?.editOpenedAt ?? ""}`;
    if (editPayment && paymentId) {
      if (lastEditPrefillKeyRef.current !== prefillKey) {
        lastEditPrefillKeyRef.current = prefillKey;
        prefillFromEditPayment(editPayment);
      }
      return;
    }

    lastEditPrefillKeyRef.current = "";
    if (isEditMode) {
      editPrefillGenRef.current += 1;
      editPrefillInFlightRef.current = false;
      setIsEditMode(false);
      setEditGatePaymentId(0);
      dispatch(removeAllPayments());
      dispatch(clearPaymentTypeDetail());
      setFilterGridRecord([]);
      setApiSubmitRecordData([]);
      setTotalAmount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.paymentId, location?.state?.editPayment, location?.state?.editOpenedAt]);

  const updatePaymentDetails = async () => {
    const gatePaymentId = Number(editGatePaymentId) || 0;
    if (!gatePaymentId) {
      dispatch(
        addNotificationAction({
          message: "Payment Id is missing for update",
          options: { variant: "error" },
        })
      );
      return;
    }

    const gatePassPaymentDetail = (apiSubmitRecordData || []).map((x: any) => ({
      charges: Number(x?.charges) || 0,
      paymentQuantity: Number(x?.paymentQuantity) || 0,
      paymentType: String(x?.paymentType ?? ""),
      paymentTypeId: Number(x?.paymentTypeId) || 0,
      currency: String(x?.currency ?? "PKR"),
      isFixed: Number(x?.isFixed) || 0,
      discountedCharges: Number(x?.discountedCharges) || 0,
      discount: Number(x?.discount) || 0,
      indexNo: Number(x?.IndexNo ?? x?.indexNo ?? formik.values.indexNumber) || 0,
      ...(x?.paymentDetailId ? { paymentDetailId: Number(x.paymentDetailId) } : {}),
    }));

    try {
      setLoader2(true);
      const res: any = await fetchWrapper.put("/add-payment-detail", {
        gatePaymentId,
        gatePassPaymentDetail,
      });
      if (res?.isSuccess) {
        // close confirmation popup + reset page after successful update
        setIsOpen(false);
        setIsEditMode(false);
        setEditGatePaymentId(0);
        resetWholePage();
        dispatch(
          addNotificationAction({
            message: res?.successMessage || "Payment updated successfully",
            options: { variant: "success" },
          })
        );
      } else {
        dispatch(
          addNotificationAction({
            message: res?.error || "Unable to update payment",
            options: { variant: "error" },
          })
        );
      }
    } catch (e: any) {
      dispatch(
        addNotificationAction({
          message: e?.message || "Unable to update payment",
          options: { variant: "error" },
        })
      );
    } finally {
      setLoader2(false);
    }
  };

  const GroupHeader = styled("div")(({}) => ({
    position: "sticky",
    top: "-8px",
    padding: "4px 10px",
    fontWeight: "bold",
    backgroundColor: "#E7E7E7",
  }));

  const GroupItems = styled("ul")({
    padding: 0,
  });

  const recordSaveHandler = (includeExistingRows = false) => {
    let grossTotal = 0;
    let discountTotal = 0;
    const shouldIncludeExisting = includeExistingRows || isEditMode;
    const invoiceType = normalizeInvoiceType(formik.values.invoiceType ?? "");
    const gridSourceRecords = (weightScanTypesRecord || []).filter((item: any) => {
      // In edit mode always keep saved invoice lines in the grid.
      if (isEditMode && item?.isExisting) return true;
      return isPaymentDetailAllowedForInvoiceType(String(item?.paymentType ?? ""), invoiceType);
    });

    if (gridSourceRecords.length) {
      const newData = gridSourceRecords.map((item: any) => {
        const isExisting = Boolean(item?.isExisting);
        const quantity = item.paymentQuantity || 1;
        const discountPerUnit = item.discount || 0;
        const totalDiscount = discountPerUnit * quantity;

        // For terminalType == 4, charges is already discounted, so use discountedCharges for gross total
        // Otherwise, charges is the original amount
        let totalCharges = 0;
        if (terminalType === 4 && item.discountedCharges !== undefined) {
          // Use original charges (discountedCharges) for gross total calculation
          totalCharges = item.discountedCharges * quantity;
        } else {
          // For non-terminalType 4, charges is the original amount
          totalCharges = item.charges * quantity;
        }

        if (!isExisting || shouldIncludeExisting) {
          grossTotal += totalCharges; // gross before discount (new rows only unless editing)
          discountTotal += totalDiscount;
        }

        return {
          ...item,
          charges: `${item.currency} ${numberFormatting(totalCharges)} `,
          chargesPerUnit: item.charges, // Preserve original charges per unit for calculations
          discount: item.discount || 0,
          discountedCharges:
            terminalType === 4 && item.discountedCharges !== undefined
              ? item.discountedCharges
              : item.charges, // Store original charges for Discount Total calculation
        };
      });

      setTotalAmount(grossTotal);
      // keep the form-level discount in sync with line-level discounts
      formik.setFieldValue("discount", discountTotal);
      setFilterGridRecord(newData);

      const newData2 = gridSourceRecords
        .filter((item: any) => (shouldIncludeExisting ? true : !item?.isExisting))
        .map((item: any) => {
          const quantity = item.paymentQuantity || 1;
          const chargesPerUnit = item.charges || 0; // This is already discounted if discount was applied
          const discountPerUnit = item.discount || 0;
          // discountedCharges is the original amount without discount
          const originalChargesPerUnit =
            terminalType === 4 && item.discountedCharges !== undefined
              ? item.discountedCharges
              : chargesPerUnit;

          return {
            ...item,
            charges: Math.round(chargesPerUnit * quantity), // charges after discount (for terminalType == 4)
            discount: Math.round(discountPerUnit * quantity),
            discountedCharges: Math.round(originalChargesPerUnit * quantity), // original charges without discount
          };
        });
      setApiSubmitRecordData(newData2);
    } else {
      setTotalAmount(0);
      setFilterGridRecord([]);
      setApiSubmitRecordData([]);
    }
  };
  const handleCloseDialog = () => {
    setIsOpen(false);
  };

  const handleConfirmDialog = async () => {
    const hasDemurrage = filterGridRecord.some((item: any) => {
      const paymentTypeStr = item.paymentType?.toString();

      return paymentTypeStr.toLowerCase().includes("demurrage");
    });
    const hasDG = filterGridRecord.some((item: any) => {
      const paymentTypeStr = item.paymentType?.toString();

      return paymentTypeStr.toLowerCase().includes("dg");
    });
    const hasStorage = filterGridRecord.some((item: any) => {
      // Check possible places where the payment type name might be
      const paymentTypeStr = item.paymentType?.toString();

      return paymentTypeStr.toLowerCase().includes("storage");
    });
    // BYD / Partywise: these validations are not applicable (no demurrage/DG/storage concept in this flow)
    if (
      !isBillGroupedInvoice &&
      containerDetails?.noOfDemurrageDays > 0 &&
      terminalType !== 4 &&
      !hasDemurrage
    ) {
      alert(
        "Container has exceeded the 5-day grace period days. Please handle port demurrage charges."
      );
      return; // stop execution
    }
    if (!isBillGroupedInvoice && containerDetails?.cargoType == "DG" && !hasDG) {
      alert("Cargo type is DG Add the DG Charges in the Payment Record before proceeding.");
      return; // stop execution
    }
    // Storage: for terminalType == 4 (loose cargo index-wise), first 8 days are free
    const storageFreeDays = terminalType === 4 ? 8 : 0;
    const chargeableStorageDays = Math.max(0, (Number(storageDays) || 0) - storageFreeDays);
    if (!isBillGroupedInvoice && chargeableStorageDays > 0 && !hasStorage) {
      alert(
        `Index has exceeded the grace period (${chargeableStorageDays} day(s)). Please handle storage charges.`
      );
      return; // stop execution
    }
    setIsOpen(false);

    // Calculate total discount: individual payment type discounts + overall discount
    let totalDiscount = Number(formik.values.discount) || 0; // Overall discount from bottom section
    // For terminalType == 4, also add individual payment type discounts
    if (terminalType === 4 && apiSubmitRecordData.length > 0) {
      const paymentDetailDiscounts = apiSubmitRecordData.reduce((sum: number, item: any) => {
        const itemDiscount =
          typeof item.discount === "string" ? parseFloat(item.discount) || 0 : item.discount || 0;
        return sum + itemDiscount;
      }, 0);
      totalDiscount += paymentDetailDiscounts;
    }

    // Calculate net amount with all discounts
    const amount = Number(totalAmount) || 0;
    const taxRate = Number(formik.values.tax.taxRate) || 0;
    const calculatedTaxAmount = (amount * taxRate) / 100;
    const finalNetAmount = amount + calculatedTaxAmount - totalDiscount;

    const billGroupedGatePass = isBillGroupedInvoiceType(formik.values.invoiceType)
      ? resolveBillGroupedGatePassFields(
          formik.values.billNumber,
          formik.values.containerNumbers,
          formik.values.containerNumber,
          virContainerList
        )
      : null;

    const record = {
      vehicleNumber: formik.values.vehicleNumber,
      gatePassId: billGroupedGatePass
        ? billGroupedGatePass.gatePassId
        : Number(formik.values.gatePassId) || 0,
      virNo: isExportMode ? formik.values.gdNumber : formik.values.virNumber,
      cargoType: (() => {
        const raw =
          containerDetails?.cargoType ??
          vehicleStatusData?.cargoType ??
          vehicleStatusData?.cargoTypeName ??
          "";
        const v = String(raw || "").trim();
        return v.length ? v : null;
      })(),
      invoiceType: formik.values.invoiceType,
      ...(shouldSendBillNumberInPayload(formik.values.invoiceType) &&
      String(formik.values.billNumber || "").trim()
        ? { billNumber: formik.values.billNumber }
        : {}),
      ...(isBillGroupedInvoiceType(formik.values.invoiceType)
        ? { gatePassIds: billGroupedGatePass?.gatePassIds ?? "" }
        : {}),
      atCommercialStation: atCommercialStation,
      createdOn: terminalType !== 4 ? formik.values.printDate : undefined,
      gatePassPaymentDetail: (apiSubmitRecordData || []).map((x: any) => ({
        ...x,
        ...(isBillGroupedInvoiceType(formik.values.invoiceType)
          ? {
              indexNo: Number(formik.values.indexNumber) || 0,
              IndexNo: Number(formik.values.indexNumber) || 0,
            }
          : {}),
        paymentTypeId: Number(x?.paymentTypeId) || 0,
        charges: Number(x?.charges) || 0,
        discount: Number(x?.discount) || 0,
        discountedCharges: Number(x?.discountedCharges) || 0,
        paymentQuantity: Number(x?.paymentQuantity) || 0,
      })),
      remarks: formik.values.remarks,
      containerNumber: formik.values.containerNumber,
      taxId: (() => {
        const raw = formik.values.taxTypeId;
        if (raw === "" || raw === null || raw === undefined) return 0;
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : 0;
      })(),
      discount: Number(formik.values.discount) || 0, // Send only overall discount, individual discounts are in gatePassPaymentDetail
      taxAmount: calculatedTaxAmount,
      taxRate: taxRate,
      netAmount: finalNetAmount,
      totalAmount: amount,
      nomination: (() => {
        const raw =
          formik.values.nominationName ||
          nominationDetails?.name ||
          containerDetails?.nominationTypeName ||
          "";
        const v = String(raw || "").trim();
        return v.length ? v : null;
      })(),
      agentId: Number(formik.values.agentNameId) || Number(containerDetails?.agentId) || 0,
      partyId: resolveForwarderIdForPayload(),
      forwarderId: resolveForwarderIdForPayload(),
    };
    const res: any = await dispatch(postTerminalChargesWholeRecordAction(record));
    if (res?.payload) {
      resetWholePage();
      formik.setFieldValue("remarks", "");
    }
  };

  const submitCompletePage = async () => {
    if (!apiSubmitRecordData.length) {
      dispatch(
        addNotificationAction({
          message: "Please add some Payment record!",
          options: { variant: "error" },
        })
      );
      return;
    }
    let data = "";
    apiSubmitRecordData.forEach((item: any) => {
      data = data.concat(
        `${item.paymentType}: ${item.currency} ${numberFormatting(item.charges)} #`
      );
    });
    // Show net amount (after discount) if discount is applied, otherwise show total amount
    const displayAmount = calculateNetAmount();
    data = data.concat(
      ` Total Charges: ${formik.values.currency} ${numberFormatting(displayAmount)}`
    );
    setDialogContent(data);
    setIsOpen(true);
  };

  function handleChange(e: any) {
    formik.handleChange(e);
    e.target.value || JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)
      ? setIsDirty(true)
      : setIsDirty(false);
  }

  const listener = (e: any) => {
    if (
      (e.key === "s" || e.key === "S") &&
      (navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey)
    ) {
      e.preventDefault();
      !loading && submitCompletePage();
    }
  };

  const exportHandler = async (row: any) => {
    dispatch(
      addNotificationAction({
        message: "Your download will begin shortly",
        options: { variant: "success" },
      })
    );
    const exportedRecords = await row?.map((data: any) => {
      return {
        "Type of Payment": data?.paymentType,
        Quantity: data?.paymentQuantity,
        "Total Charges": `PKR ${data?.charges?.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}`,
      };
    });
    return exportedRecords;
  };

  const handleCloseDublicateDialog = () => {
    setIsOpenDublicateDailog(false);
    const deleteGridItemId: any = weightScanTypesRecord[weightScanTypesRecord.length - 1];
    dispatch(removeGridItem(deleteGridItemId.id));
  };

  /*************  ✨ Windsurf Command ⭐  *************/
  /**
   * Closes the duplicate dialog and triggers the record save handler.
   * This function is asynchronous and ensures the dialog is closed
   * before proceeding to handle the record saving logic.
   */

  /*******  60c74571-e947-4317-93dd-71e3261aa549  *******/
  const handleSaveDublicateDialog = async () => {
    setIsOpenDublicateDailog(false);
    recordSaveHandler();
  };

  function calculateNetAmount() {
    const amount = Number(totalAmount) || 0;
    const taxRate = Number(formik.values.tax.taxRate) || 0;

    // Calculate total discount: individual payment type discounts + overall discount
    let totalDiscount = Number(formik.values.discount) || 0; // Overall discount from bottom section

    // For terminalType == 4, also add individual payment type discounts
    if (terminalType === 4 && apiSubmitRecordData.length > 0) {
      const paymentDetailDiscounts = apiSubmitRecordData.reduce((sum: number, item: any) => {
        const itemDiscount =
          typeof item.discount === "string" ? parseFloat(item.discount) || 0 : item.discount || 0;
        return sum + itemDiscount;
      }, 0);
      totalDiscount += paymentDetailDiscounts;
    }

    const taxAmount = (amount * taxRate) / 100;

    // Calculate net amount: gross total + tax - all discounts
    const netAmount = amount + taxAmount - totalDiscount;

    return netAmount;
  }
  function calculateTax() {
    const tax = Number(totalAmount) * (Number(formik.values.tax.taxRate) / 100);
    return tax;
  }

  const fetchPendingContainers = async () => {
    setPendingContainerList([]);
    try {
      const invoiceTypeForReleasedVirs =
        formik.values.invoiceType === "Partywise" ? "Normal" : formik.values.invoiceType;
      const results = await fetchWrapper.post(
        `get-all-released-virs?invoiceType=${encodeURIComponent(invoiceTypeForReleasedVirs)}`,
        {}
      );
      if (results.isSuccess) {
        setPendingContainerList(results.value.list);
      }
      return;
    } catch (error) {}
  };

  const searchVirNumber = async (virNo: string) => {
    try {
      const results: any = await fetchWrapper.get(`search-vir-number/${virNo}`);
      if (results.isSuccess) {
        setFuturePaymentVirList(results.value.virList || []);
      }
    } catch (error) {}
  };
  const calculateStorageCharge = (slabs: any[], totalStorageDays: number, freeDays: number) => {
    const chargeableDays = Math.max(0, totalStorageDays - freeDays);
    if (chargeableDays === 0) return 0; // no charge if all free

    let charge = 0;
    const chargeStartDay = freeDays + 1; // first chargeable day

    for (const slab of slabs) {
      // Overlap between slab and chargeable days
      const start = Math.max(slab.dayStart, chargeStartDay);
      const end = Math.min(slab.dayEnd, totalStorageDays);

      if (end >= start) {
        const daysInSlab = end - start + 1;
        charge += daysInSlab * slab.charges;
      }
    }
    return charge;
  };
  const fetchStorageSlabs = async (type: any) => {
    try {
      const results = await fetchWrapper.get(
        `get-storage-slabs_by_type?Type=${type}&IsActive=true`
      );
      if (results.isSuccess && results.value?.slabs) {
        // Handle the slabsList as needed (e.g., set to state)
        setStorageSlabs(results.value.slabs);
        return results.value.slabs;
      }
    } catch (error) {
      console.error("Error fetching storage slabs:", error);
    }
  };

  const resolveManifestBillNo = (
    indexNumber: any,
    gatePassId?: any,
    contextRow?: any,
    containerDetail?: any
  ): string => {
    const fromCandidates = [
      formik.values.billNumber,
      contextRow?.billNumber,
      contextRow?.billNo,
      contextRow?.BillNo,
      contextRow?.blNumber,
      contextRow?.bL_No,
      containerDetail?.blNumber,
      containerDetail?.billNumber,
      containerDetail?.billNo,
      containerDetail?.BillNo,
    ];
    for (const candidate of fromCandidates) {
      const v = String(candidate ?? "").trim();
      if (v) return v;
    }

    const indexStr = String(indexNumber ?? "").trim();
    if (!indexStr) return "";

    const vir = String(formik.values.virNumber || "").trim();
    const gatePassStr = gatePassId != null && gatePassId !== "" ? String(gatePassId) : "";

    const match = (virContainerList || []).find((r: any) => {
      if (String(r?.indexNumber ?? "") !== indexStr) return false;
      if (gatePassStr) {
        const rowGatePass = String(r?.gatePassId ?? r?.gateEntryId ?? "");
        if (rowGatePass && rowGatePass !== gatePassStr) return false;
      }
      if (vir) {
        const rowVir = String(r?.virNo ?? r?.virNumber ?? r?.vir ?? "");
        if (rowVir && rowVir !== vir) return false;
      }
      return true;
    });

    if (!match) return "";
    return String(
      match?.billNumber ?? match?.billNo ?? match?.BillNo ?? match?.blNumber ?? match?.bL_No ?? ""
    ).trim();
  };

  const resolveTerminalId = () => {
    const raw = Number(localStorage.getItem("terminalId"));
    return Number.isFinite(raw) ? raw : 0;
  };

  const applyContainerDetailsFromApi = (
    details: any,
    indexNumber: any,
    gatePassId: any,
    merge = false
  ) => {
    const value = details?.value?.value ?? details?.value ?? details ?? {};
    if (merge) {
      setContainerDetails((prev: any) => ({ ...(prev || {}), ...value }));
      applyInvoiceManifestSideEffects(value);
    } else {
      setContainerDetails(value);
    }
    if (isPartywiseInvoice) {
      const gateInRaw = resolveRowGateInRaw(value, "CY");
      const containerSize = resolveRowContainerSize(value);
      const cn =
        Array.isArray(formik.values.containerNumbers) && formik.values.containerNumbers.length
          ? String(formik.values.containerNumbers[0] || "").trim()
          : String(value?.containerNumber || "").trim();
      const cacheKey = partywiseContainerCacheKey(
        { gatePassId, indexNumber, containerNumber: cn },
        cn
      );
      if (gateInRaw || containerSize) {
        setPartywiseContainerMetaCache((prev) => ({
          ...prev,
          [cacheKey]: {
            ...prev[cacheKey],
            ...(gateInRaw ? { gateInDate: String(gateInRaw) } : {}),
            ...(containerSize ? { containerSize } : {}),
          },
        }));
      }
    }
    if (!merge && !isBydInvoice && !isPartywiseInvoice && terminalType === 4) {
      const forwarderRaw = value?.forwarder || "";
      formik.setFieldValue("PartyId", resolveForwarderDisplayName(forwarderRaw));
      return getNominationsByParty(forwarderRaw);
    }
  };

  const getInvoiceManifestDetails = async (
    virNo: string,
    billNo: string,
    indexNo: string | number,
    gatePassId?: any
  ) => {
    const fetchGen = ++invoiceManifestFetchGenRef.current;
    try {
      const results: any = await fetchWrapper.post("get-invoice-manifest-details", {
        virNo: String(virNo).trim(),
        billNo: String(billNo).trim(),
        indexNo: Number(indexNo) || 0,
        terminalId: resolveTerminalId(),
      });
      if (fetchGen !== invoiceManifestFetchGenRef.current) return;
      if (results?.isSuccess) {
        await applyContainerDetailsFromApi(results, indexNo, gatePassId, true);
      }
    } catch (error) {
      console.error("Failed to fetch invoice manifest details", error);
    }
  };

  const getContailerDetails = async (indexNumber: any, gatePassId: any, contextRow?: any) => {
    if (shouldRequireDestuffing(formik.values.invoiceType) && isDestuffingPending(contextRow)) {
      notifyDestuffingRequired();
      return;
    }

    const virNo = String(formik.values.virNumber || "").trim();
    const indexRaw = indexNumber;
    const hasGatePassIndex =
      gatePassId != null && gatePassId !== "" && indexRaw != null && String(indexRaw).trim() !== "";

    setContainerDetails([]);
    setnominationDetails({});
    setLoader2(true);

    let containerDetailValue: any = null;

    try {
      if (isFuturePaymentInvoice && contextRow && !hasGatePassIndex) {
        containerDetailValue = mapPendingCheckoutIndexRow(contextRow);
        setContainerDetails(containerDetailValue);
      }

      if (hasGatePassIndex) {
        const results = await fetchWrapper.get(
          `get-container-detail/${gatePassId}/index_no/${indexNumber}`
        );
        if (results.isSuccess) {
          containerDetailValue = results?.value?.value ?? results?.value ?? null;
          await applyContainerDetailsFromApi(results, indexNumber, gatePassId);
        }
      }

      const billNo = resolveManifestBillNo(indexRaw, gatePassId, contextRow, containerDetailValue);
      const hasManifestKeys =
        Boolean(virNo) && Boolean(billNo) && indexRaw != null && String(indexRaw).trim() !== "";

      if (hasManifestKeys) {
        await getInvoiceManifestDetails(virNo, billNo, indexRaw, gatePassId);
      }
    } catch (error) {
      console.error("Failed to fetch container details", error);
    } finally {
      setLoader2(false);
    }
  };

  useEffect(() => {
    if (!isEditMode) return;
    if (editPrefillInFlightRef.current) return;
    const { indexNumber: indexNo, gatePassId } = formik.values;
    if (indexNo == null || String(indexNo).trim() === "" || !gatePassId) return;

    const t = window.setTimeout(() => {
      void getContailerDetails(indexNo, gatePassId);
    }, 350);

    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditMode,
    formik.values.gatePassId,
    formik.values.indexNumber,
    formik.values.virNumber,
    formik.values.billNumber,
  ]);

  useEffect(() => {
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [apiSubmitRecordData, loading]);

  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);

  useEffect(() => {
    if (editPrefillInFlightRef.current) return;
    const invoiceType = normalizeInvoiceType(formik.values.invoiceType);
    const staleRows = (weightScanTypesRecord || []).filter(
      (item: any) =>
        !item?.isExisting &&
        !isPaymentDetailAllowedForInvoiceType(String(item?.paymentType ?? ""), invoiceType)
    );
    if (!staleRows.length) return;
    staleRows.forEach((item: any) => {
      if (item?.id != null) dispatch(removeGridItem(item.id));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weightScanTypesRecord, formik.values.invoiceType]);

  useEffect(() => {
    if (editPrefillInFlightRef.current) return;
    const activePaymentTypeId = Number(formik.values.paymentTypeId) || 0;
    if (activePaymentTypeId > 0 && !isEditMode) {
      const duplicateCount =
        weightScanTypesRecord?.filter(
          (rec: any) =>
            !rec?.isTariffBreakdown && Number(rec?.paymentTypeId) === activePaymentTypeId
        ).length ?? 0;
      if (duplicateCount > 1) {
        setIsOpenDublicateDailog(true);
        return;
      }
    }
    recordSaveHandler(isEditMode);
  }, [weightScanTypesRecord, isEditMode, formik.values.paymentTypeId, editPrefillSyncTick]);

  useEffect(() => {
    if (!isEditMode || editPrefillInFlightRef.current) return;

    const records = (weightScanTypesRecord || []) as any[];
    if (!records.length) return;

    const tariffParent = getTariffParentFromCatalog(paymentTypes);
    if (!tariffParent) return;

    const needsRelabel = records.some(
      (row) =>
        row?.isExisting &&
        Number(row?.paymentTypeId) === Number(tariffParent.id) &&
        String(row?.paymentType ?? "")
          .trim()
          .toLowerCase() === TARIFF_PAYMENT_NAME.toLowerCase()
    );
    if (!needsRelabel) return;

    const nominationObj = getNominationForCharges();
    if (
      !Number(nominationObj?.perCbm) &&
      !Number(nominationObj?.terminalHandlingCharges) &&
      !Number(nominationObj?.perIndex) &&
      !Number(nominationObj?.documentationCharges) &&
      !Number(nominationObj?.fuelCharges) &&
      !Number(nominationObj?.trackingCharges)
    ) {
      return;
    }

    const relabelKey = `${editGatePaymentId}:${editPrefillSyncTick}:${records.length}`;
    if (tariffRelabelKeyRef.current === relabelKey) return;

    const relabeled = relabelTariffBreakdownDetails(records, {
      catalog: paymentTypes,
      nominationObj,
      indexCbm: Number(containerDetails?.billingCBM) || numberOfCbm || 0,
      paymentQuantity: 1,
      isPartywise: String(formik.values.invoiceType ?? "").toUpperCase() === "PARTYWISE",
      totalIndexes: Number(containerDetails?.totalIndexes) || 1,
    });

    const changed = relabeled.some(
      (row, index) => row?.paymentType !== records[index]?.paymentType
    );
    if (!changed) return;

    tariffRelabelKeyRef.current = relabelKey;
    dispatch(removeAllPayments());
    relabeled.forEach((row) => dispatch(addPaymentAction(row)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditMode,
    editGatePaymentId,
    editPrefillSyncTick,
    weightScanTypesRecord,
    nominationDetails,
    containerDetails,
    paymentTypes,
    numberOfCbm,
    formik.values.invoiceType,
  ]);

  useEffect(() => {
    if (terminalType === 4) {
      getVehicleContainerNumbers();
      document.addEventListener("keydown", listener);
      return () => {
        document.removeEventListener("keydown", listener);
      };
    }
  }, []);
  useEffect(() => {
    (async () => {
      if (skipPaymentTypeAutoChargeRef.current) {
        skipPaymentTypeAutoChargeRef.current = false;
        return;
      }
      // const storagePaymentType = paymentTypeDetail?.name?.toLowerCase().includes("storage");
      if (paymentTypeDetail && terminalType !== 4) {
        // const indexCbm = containerDetails?.billingCBM || numberOfCbm || 0;
        // const nominationObj = nominationDetails || {};
        const paymentName = paymentTypeDetail.name;
        if (paymentName === "Port Demurrage Charges") {
          const totalIndexes = containerDetails?.totalIndexes || 1;
          const noOfDemurrageDays = containerDetails?.noOfDemurrageDays || 0;
          const amountPerContainer = paymentTypeDetail?.amount / totalIndexes;
          const finalAmount = Math.round(amountPerContainer * noOfDemurrageDays);

          formik.setFieldValue("charges", finalAmount);
          setOriginalCharges(finalAmount);
        } else if (paymentName === "Detention Charges") {
          const unpaidCount = containerDetails?.unpaidIndexCount || 1;
          const amount = Math.round((paymentTypeDetail?.amount || 0) / unpaidCount);
          formik.setFieldValue("charges", amount);
          setOriginalCharges(amount);
          // Calculate Storage Charges
        } else {
          formik.setFieldValue("charges", paymentTypeDetail?.amount);
          setOriginalCharges(paymentTypeDetail?.amount || 0);
        }
        // formik.setFieldValue("charges", paymentTypeDetail.amount);
      } else {
        const paymentName = paymentTypeDetail?.name || "";
        const isStoragePerTonPerDay =
          paymentName.toLowerCase().trim() === "storage payment per ton/day".toLowerCase();

        // Only calculate after the user explicitly selects a payment type
        if (hasUserSelectedPaymentType && terminalType === 4 && isStoragePerTonPerDay) {
          const ratePerTonPerDay = Number(paymentTypeDetail?.amount) || 728;
          const freeDays = 8;
          const days = Math.max(0, (Number(storageDays) || 0) - freeDays);
          const weightKgRaw = String(formik.values.indexWeight ?? "").replace(/,/g, "");
          const weightKg = Number(weightKgRaw) || 0;
          const weightTons = Math.ceil(weightKg / 1000);
          const amount = Math.round(ratePerTonPerDay * days * weightTons);

          formik.setFieldValue("charges", amount);
          setOriginalCharges(amount);
        } else {
          const amount = paymentTypeDetail?.amount || 0;
          formik.setFieldValue("charges", amount);
          setOriginalCharges(amount);
        }
      }
    })();
  }, [paymentTypeDetail, hasUserSelectedPaymentType]);

  // Recalculate storage-per-ton/day when inputs change (terminalType == 4 only)
  useEffect(() => {
    const paymentName = paymentTypeDetail?.name || "";
    const isStoragePerTonPerDay =
      paymentName.toLowerCase().trim() === "storage payment per ton/day".toLowerCase();
    if (!(hasUserSelectedPaymentType && terminalType === 4 && isStoragePerTonPerDay)) return;

    const ratePerTonPerDay = Number(paymentTypeDetail?.amount) || 728;
    const freeDays = 8;
    const days = Math.max(0, (Number(storageDays) || 0) - freeDays);
    const weightKgRaw = String(formik.values.indexWeight ?? "").replace(/,/g, "");
    const weightKg = Number(weightKgRaw) || 0;
    const weightTons = Math.ceil(weightKg / 1000);
    const amount = Math.round(ratePerTonPerDay * days * weightTons);

    formik.setFieldValue("charges", amount);
    setOriginalCharges(amount);
  }, [
    terminalType,
    paymentTypeDetail?.name,
    paymentTypeDetail?.amount,
    storageDays,
    formik.values.indexWeight,
    hasUserSelectedPaymentType,
  ]);
  useEffect(() => {
    setOriginalCharges(0);
    formik.setFieldValue("charges", "");
  }, []);
  useEffect(() => {
    if (isPartywiseInvoice) {
      setStorageDays(partywiseStorageMetrics.baseStorageDays);
      return;
    }

    const destuffDateStr = containerDetails?.destuffingDate;
    const gateinDate = containerDetails?.gateInDate || containerDetails?.createdDateTime;
    let nextStorageDays = 0;
    const asOfDate =
      terminalType === 4 ? new Date() : effectiveChargesDateForCalculations ?? new Date();

    // Loose cargo (terminalType == 4): "Storage Payment Per Ton/Day" — inclusive days, 8 free (see charge effect).
    if (terminalType === 4) {
      const startStr = gateinDate || destuffDateStr;
      if (startStr) {
        const start = parseBackendDateToLocal(startStr);
        if (start) {
          nextStorageDays = Math.max(0, diffCalendarDaysInclusiveStorageTonDay(start, asOfDate));
        }
      }
    } else if (containerDetails?.containerType === "CY") {
      if (gateinDate) {
        const createdDate = parseBackendDateToLocal(gateinDate);
        if (createdDate) nextStorageDays = Math.max(0, diffCalendarDays(createdDate, asOfDate));
      }
    } else {
      if (destuffDateStr) {
        const createdDate = parseBackendDateToLocal(destuffDateStr);
        if (createdDate) nextStorageDays = Math.max(0, diffCalendarDays(createdDate, asOfDate));
      }
    }
    setStorageDays(nextStorageDays);
  }, [
    containerDetails,
    effectiveChargesDateForCalculations,
    terminalType,
    isPartywiseInvoice,
    partywiseStorageMetrics.baseStorageDays,
  ]);

  useEffect(() => {
    if (!formik.values.paymentTypeId) return;
    const filtered = paymentTypesList.filter((pt: any) => Number(pt?.id) !== -1);
    const stillValid = filtered.some(
      (pt: any) =>
        Number(pt?.id) === Number(formik.values.paymentTypeId) ||
        String(pt?.name ?? "") === String(formik.values.paymentType ?? "")
    );
    if (!stillValid) {
      formik.setFieldValue("paymentTypeId", 0);
      formik.setFieldValue("paymentType", "");
      formik.setFieldValue("charges", "");
      setOriginalCharges(0);
      dispatch(clearPaymentTypeDetail());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentTypesList, formik.values.paymentTypeId, formik.values.paymentType]);

  useEffect(() => {
    // Filter out discount and discountTotal columns if terminalType !== 4
    const filteredColumns =
      terminalType === 4
        ? columns
        : columns.filter(
            (column: any) =>
              column.accessorKey !== "discount" && column.accessorKey !== "discountTotal"
          );

    const transformedColumns: any = filteredColumns.map((column: any) =>
      column.accessorKey === "actionButtons"
        ? {
            ...column,
            Cell: function customCell({ row }: any) {
              const disableDelete = Boolean(row?.original?.isExisting);
              return (
                <>
                  <Box
                    component="span"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                    }}
                  >
                    <NewCommonButton
                      icon={<DeleteIcon />}
                      isDelete={true}
                      disable={disableDelete}
                      onClick={() => {
                        if (!disableDelete) handleRemoveGridItem(row.original);
                      }}
                    >
                      Delete
                    </NewCommonButton>
                  </Box>
                </>
              );
            },
          }
        : column
    );
    setDisplayColumns(transformedColumns);
  }, [terminalType]);

  useEffect(() => {
    dispatch(getPaymentTypesDropdownAction());
    fetchPendingContainers();
    dispatch(getAllTaxTypes(1));
    if (isExportMode) {
      getVehicleGDs();
    }
  }, [isExportMode]);

  useEffect(() => {
    if (!isFuturePaymentInvoice) {
      fetchPendingContainers();
    }
  }, [formik.values.invoiceType, isFuturePaymentInvoice]);

  /* eslint-enable react-hooks/rules-of-hooks */

  const hasTariffPaymentInGrid = () => {
    const payment = paymentTypesList.find((pt: any) => pt.name === TARIFF_PAYMENT_NAME);
    if (!payment) return false;
    return (weightScanTypesRecord || []).some(
      (rec: any) =>
        rec?.isTariffBreakdown ||
        (Number(rec?.paymentTypeId) === Number(payment.id) &&
          String(rec?.paymentType || "").trim() === TARIFF_PAYMENT_NAME)
    );
  };

  // Helper function to calculate tariff payment charges
  const calculateTariffPayment = async (values: any, actions: any) => {
    if (extraValidations(values, isOtherPayment) === false) return;
    const payment = paymentTypesList.find((pt: any) => pt.name === TARIFF_PAYMENT_NAME);
    if (!payment) {
      dispatch(
        addNotificationAction({
          message: "Tarrif Payment type is not configured.",
          options: { variant: "error" },
        })
      );
      return;
    }
    if (hasTariffPaymentInGrid()) {
      dispatch(
        addNotificationAction({
          message: "Tarrif Payment has already been added.",
          options: { variant: "error" },
        })
      );
      return;
    }

    const indexCbm = containerDetails?.billingCBM || numberOfCbm || 0;
    const nominationObj = getNominationForCharges();

    // For Partywise invoices, tracking charges are billed per bill (not per index).
    // Keep existing behavior for all other invoice types.
    const invoiceTypeUpper = String(values.invoiceType ?? "").toUpperCase();
    const isPartywiseForTariff = invoiceTypeUpper === "PARTYWISE";
    const trackingChargesForTariff =
      nominationObj.trackingCharges > 0
        ? isPartywiseForTariff
          ? // Quantity for Partywise is container-count (not index-count). Since totals are calculated as:
            //   totalCharges = chargesPerUnit * paymentQuantity
            // we must prorate tracking charges using paymentQuantity, not totalIndexes.
            nominationObj.trackingCharges / (values.paymentQuantity || 1)
          : nominationObj.trackingCharges / (containerDetails?.totalIndexes || 1)
        : 0;

    const breakdownLines = buildTariffPaymentBreakdown(
      nominationObj,
      indexCbm,
      trackingChargesForTariff,
      values.paymentQuantity,
      isPartywiseForTariff
    );

    if (!breakdownLines.length) {
      dispatch(
        addNotificationAction({
          message: "No tariff charges found for the selected nomination.",
          options: { variant: "error" },
        })
      );
      return;
    }

    breakdownLines.forEach((line) => {
      dispatch(
        addPaymentAction({
          IndexNo: values.indexNumber,
          ...billNumberPayloadFields(values.invoiceType, values.billNumber),
          currency: values.currency,
          paymentTypeId: payment.id,
          paymentType: line.label,
          isFixed: payment.isFixed,
          otherPaymentName: values.otherPaymentName || "",
          paymentQuantity: line.paymentQuantity,
          charges: line.charges,
          isTariffBreakdown: true,
          tariffParentPaymentType: TARIFF_PAYMENT_NAME,
        })
      );
    });

    setVehicleAdd(true);
    actions.setSubmitting(false);
    setIsDirty(false);
    resetFilterForm();
  };

  const calculateBydCharges = async (values: any) => {
    if (terminalType === 4) return;
    if (!isBydInvoice) return;

    const containerCount = Array.isArray(values.containerNumbers)
      ? values.containerNumbers.length
      : 0;
    if (!values.virNumber || !values.billNumber || containerCount <= 0) return;

    const bydChargeNames = [
      "Container Services Charges",
      "Port Scanning Charges",
      "Tracking & RFID Charges",
    ];

    const missing = bydChargeNames.filter(
      (n) => !paymentTypesList.some((pt: any) => String(pt?.name) === n)
    );
    if (missing.length) {
      dispatch(
        addNotificationAction({
          message: `Missing BYD charges in configuration: ${missing.join(", ")}`,
          options: { variant: "error" },
        })
      );
      return;
    }

    for (const name of bydChargeNames) {
      const payment = paymentTypesList.find((pt: any) => String(pt?.name) === name);
      if (!payment) continue;

      const amountPerContainer = Number(payment?.amount) || 0;
      const obj = {
        IndexNo: values.indexNumber,
        ...billNumberPayloadFields(values.invoiceType, values.billNumber),
        currency: values.currency,
        paymentTypeId: payment.id,
        paymentType: payment.name,
        isFixed: payment.isFixed,
        otherPaymentName: values.otherPaymentName || "",
        // These BYD charges are charged per container; show actual container count in Quantity.
        // Store per-container amount so downstream total calculations remain correct.
        paymentQuantity: containerCount,
        charges: Math.round(amountPerContainer),
      };
      dispatch(addPaymentAction(obj));
    }
    setVehicleAdd(true);
  };
  // Helper function to calculate Examination payment charges
  const calculateExaminationCharges = async (values: any, actions: any) => {
    if (extraValidations(values, isOtherPayment) === false) return;
    const payment = paymentTypesList.find((pt: any) => pt.name === "Examination Charges");
    const temp = payment.name;
    if (isOtherPayment) payment.name = values.otherPaymentName;

    const obj = {
      IndexNo: values.indexNumber,
      ...billNumberPayloadFields(values.invoiceType, values.billNumber),
      currency: values.currency,
      paymentTypeId: payment.id,
      paymentType: payment.name,
      isFixed: payment.isFixed,
      otherPaymentName: values.otherPaymentName || "",
      paymentQuantity: values.paymentQuantity,
      charges: payment.amount,
    };

    const res: any = await dispatch(addPaymentAction(obj));
    setVehicleAdd(true);

    if (res.payload) {
      values.paymentType = temp;
      actions.setSubmitting(false);
      setIsDirty(false);
      resetFilterForm();
      return;
    }
  };
  /**
   * Slab-based "Storage Charges" (e.g. CFS LCL General 5 free days, CY 10 free days).
   * Separate from invoice-line "Storage Payment Per Ton/Day" (terminalType 4, 8 free days, ton/day rate).
   */
  const calculateStoragePayment = async (values: any, actions: any) => {
    let amount = 0;
    const isPartywise = values.invoiceType === "Partywise";

    if (isPartywise && partywiseStorageMetrics.containerCount <= 0) {
      dispatch(
        addNotificationAction({
          message: "Please select at least one container for Partywise storage charges.",
          options: { variant: "error" },
        })
      );
      return;
    }

    //storage charges for CY containers
    if (!isExportMode) {
      if (isPartywise) {
        const baseDays = partywiseStorageMetrics.baseStorageDays;
        const slabCache = new Map<string, any[]>();

        for (const container of partywiseStorageMetrics.containers) {
          const slabKey = resolvePartywiseCySlabKey(container.containerSize);
          if (!slabKey) continue;

          if (!slabCache.has(slabKey)) {
            const slabs = await fetchStorageSlabs(slabKey);
            if (slabs) slabCache.set(slabKey, slabs);
          }

          const slabs = slabCache.get(slabKey);
          if (slabs) {
            amount += calculateStorageCharge(slabs, baseDays, 10);
          }
        }
      } else if (containerDetails?.containerType === "CY") {
        const gateInDate = new Date(containerDetails?.gateInDate); // example gate-in date
        const currentDate = effectiveChargesDateForCalculations;

        // Calculate difference in milliseconds
        const diffInMs = currentDate.getTime() - gateInDate.getTime();
        const diffDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (containerDetails?.containerSize === 45) {
          const slabs = await fetchStorageSlabs("CY45");
          amount = calculateStorageCharge(slabs, diffDays, 10);
        }
        if (containerDetails?.containerSize === 40) {
          const slabs = await fetchStorageSlabs("CY40");
          amount = calculateStorageCharge(slabs, diffDays, 10);
        }
        if (containerDetails?.containerSize === 20) {
          const slabs = await fetchStorageSlabs("CY20");
          amount = calculateStorageCharge(slabs, diffDays, 10);
        }
      }
      //storage charges for CFS containers
      if (containerDetails?.containerType === "CFS") {
        const gateInDate = new Date(containerDetails?.destuffingDate); // example gate-in date
        const currentDate = effectiveChargesDateForCalculations;

        // Calculate difference in milliseconds
        const diffInMs = currentDate.getTime() - gateInDate.getTime();
        const diffDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (containerDetails?.cargoType === "DG") {
          const slabs = await fetchStorageSlabs("CFS LCL(DG)");
          amount = calculateStorageCharge(slabs, diffDays, 3);
        }
        if (containerDetails?.cargoType === "General") {
          const slabs = await fetchStorageSlabs("CFS LCL(General)");
          amount = calculateStorageCharge(slabs, diffDays, 5);
        }
        if (containerDetails?.cargoType === "Cars") {
          const slabs = await fetchStorageSlabs("CFS Car");
          amount = calculateStorageCharge(slabs, diffDays, 12);
        }
      }
      if (containerDetails?.containerType === "CFS Scrap") {
        const gateInDate = new Date(containerDetails?.createdDateTime); // example gate-in date
        const currentDate = effectiveChargesDateForCalculations;

        // Calculate difference in milliseconds
        const diffInMs = currentDate.getTime() - gateInDate.getTime();
        const diffDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
        if (diffDays > 37) {
          const billableDays = diffDays - 37;
          amount = billableDays * 80 * (containerDetails.cargoWeight / 1000);
        }
      }
    }

    //storage charges for CFS LCL(General) Export mode
    if (isExportMode) {
      const gateInDate = new Date(vehicleStatusData?.dateTime); // example gate-in date
      const currentDate = effectiveChargesDateForCalculations;

      // Calculate difference in milliseconds
      const diffInMs = currentDate.getTime() - gateInDate.getTime();
      const diffDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      if (vehicleStatusData?.cargoType === "LOOSE CARGO") {
        const slabs = await fetchStorageSlabs("CFS LCL(General)");
        amount = calculateStorageCharge(slabs, diffDays, 5);
      }
    }
    //storage charges end for CFS LCL(General) Export mode
    //storage charges for CY containers EXport mode
    if (isExportMode && vehicleStatusData?.containerType) {
      const gateInDate = new Date(vehicleStatusData?.dateTime);
      const currentDate = effectiveChargesDateForCalculations;

      // Calculate difference in days
      const diffInMs = currentDate.getTime() - gateInDate.getTime();
      const diffDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      let slabKey = "";
      let freeDays = 5; // default for General

      // Determine slab key based on container type and size
      if (vehicleStatusData.cargoType === "GENERAL") {
        if (vehicleStatusData.containerSize === 45) slabKey = "CY45";
        if (vehicleStatusData.containerSize === 40) slabKey = "CY40";
        if (vehicleStatusData.containerSize === 20) slabKey = "CY20";
      }

      if (vehicleStatusData.cargoType === "DG") {
        freeDays = 2;
        if (vehicleStatusData.containerSize === 45) slabKey = "CY(DG)45";
        if (vehicleStatusData.containerSize === 40) slabKey = "CY(DG)40";
        if (vehicleStatusData.containerSize === 20) slabKey = "CY(DG)20";
      }

      if (slabKey) {
        const slabs = await fetchStorageSlabs(slabKey);
        amount = calculateStorageCharge(slabs, diffDays, freeDays);
      }
    }

    //storage charges end for CY containers EXport mode
    if (extraValidations(values, isOtherPayment) === false) return;
    const payment = paymentTypesList.find((pt: any) => pt.name === "Storage Charges");
    const temp = payment.name;
    if (isOtherPayment) payment.name = values.otherPaymentName;
    // Partywise: storage uses latest gate-in days × selected container count (single line item).
    const storagePaymentQuantity = values.invoiceType === "Partywise" ? 1 : values.paymentQuantity;

    const obj = {
      IndexNo: values.indexNumber,
      ...billNumberPayloadFields(values.invoiceType, values.billNumber),
      currency: values.currency,
      paymentTypeId: payment.id,
      paymentType: payment.name,
      isFixed: payment.isFixed,
      otherPaymentName: values.otherPaymentName || "",
      paymentQuantity: storagePaymentQuantity,
      charges: amount,
    };

    const res: any = await dispatch(addPaymentAction(obj));
    setVehicleAdd(true);

    if (res.payload) {
      values.paymentType = temp;
      actions.setSubmitting(false);
      setIsDirty(false);
      resetFilterForm();
      return;
    }
  };
  const calculateDGCargoPayment = async (values: any, actions: any) => {
    if (extraValidations(values, isOtherPayment) === false) return;
    const payment = paymentTypesList.find((pt: any) => pt.name === "DG Cargo Payment");
    const temp = payment.name;
    if (isOtherPayment) payment.name = values.otherPaymentName;

    const obj = {
      IndexNo: values.indexNumber,
      ...billNumberPayloadFields(values.invoiceType, values.billNumber),
      currency: values.currency,
      paymentTypeId: payment.id,
      paymentType: payment.name,
      isFixed: payment.isFixed,
      otherPaymentName: values.otherPaymentName || "",
      paymentQuantity: values.paymentQuantity,
      charges: payment.amount,
    };

    const res: any = await dispatch(addPaymentAction(obj));
    setVehicleAdd(true);

    if (res.payload) {
      values.paymentType = temp;
      actions.setSubmitting(false);
      setIsDirty(false);
      resetFilterForm();
      return;
    }
  };

  const getVehicleByRegistrationNo = async (queryString: string) => {
    let results: any = [];
    results = await fetchWrapper.get(
      `search-vehicles-by-vehicle-registration-number/${queryString}`
    );
    if (results.isSuccess) {
      setvehicleRegNumberArr(results.value);
    }
    return;
  };
  // ADD THIS FUNCTION FROM SCENARIO 2
  const getVehicleStatusByVehicleId = async (id: number) => {
    let results: any = [];
    try {
      setLoader2(true);
      results = await fetchWrapper.get(`get-vehicle-status-id-by-vehicle-Id/${id}`);

      if (results.isSuccess) {
        // Store full vehicle status data
        setVehicleStatusData(results.value || {});
        // setcargoType(results.value.cargoTypeName);
        // setconsignmentType(results.value.consignmentTypeName);
        // setvehicleType(results.value.vehicleType);
        // setentryDateTime(results.value.dateTime);
        // setScanning(results.value.isScanComplete);
        // setWeight(results.value.isWeightDone);
        // Pre-fill container number and gate pass for export flow
        const containerNumber = results.value.containerNumber || "";
        const gatePassId = results.value.gatePassIds || "";
        if (containerNumber && isExportMode) {
          formik.setFieldValue("containerNumber", containerNumber);
          formik.setFieldValue("gatePassId", gatePassId);
          setGdContainerList([{ containerNumber, gatePassId }]);
        } else if (!isExportMode) {
          setGdContainerList([]);
        }
      }
    } finally {
      setLoader2(false);
    }
  };
  const getVehicleGDs = async () => {
    try {
      setLoader2(true);
      const results = await fetchWrapper.post("get-all-pending-gds", {});
      if (results.isSuccess) {
        setGdList(results.value.getAllPendingGds || []);
      }
    } catch (error) {
      console.error("Failed to fetch GD list", error);
    } finally {
      setLoader2(false);
    }
  };

  // const getVehiclesByGD = async (gdNo: string) => {
  //   try {
  //     setLoader2(true);
  //     const results = await fetchWrapper.post("get-all-details-by-gd", { gdNo });
  //     if (results.isSuccess) {
  //       setGdVehiclesList(results.value.getAllVehicleNumbers || []);
  //     }
  //   } catch (error) {
  //     console.error("Failed to fetch vehicles by GD", error);
  //     setGdVehiclesList([]);
  //   } finally {
  //     setLoader2(false);
  //   }
  // };

  // const getContainersByVehicleAndGD = async (vehicleNumber: string, gdNo: string) => {
  //   try {
  //     setLoader2(true);
  //     const results = await fetchWrapper.post("get-all-containsers-by-gd-or-vir", {
  //       id: vehicleNumber,
  //       consignmentType: "Export",
  //       virNo: gdNo,
  //       isEdit: false,
  //     });
  //     if (results.isSuccess) {
  //       const containers = results.value.containersList || [];
  //       setGdContainerList(containers);
  //       // Auto-populate container number if only one container
  //       if (containers.length === 1) {
  //         formik.setFieldValue("containerNumber", containers[0].containerNumber || "");
  //         formik.setFieldValue("gatePassId", containers[0].gatePassId || "");
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Failed to fetch containers", error);
  //     setGdContainerList([]);
  //   } finally {
  //     setLoader2(false);
  //   }
  // };
  const checkStorageCharges = (row: any) => {
    const destuffDateStr = row?.destuffingDate;
    const gateinDate = row?.gateInDate || row?.createdDateTime;
    let storageDays = 0;
    if (destuffDateStr || gateinDate) {
      if (row?.containerType === "CY") {
        const createdDate = parseBackendDateToLocal(gateinDate);
        const asOfDate =
          terminalType === 4 ? new Date() : effectiveChargesDateForCalculations ?? new Date();
        if (createdDate) storageDays = Math.max(0, diffCalendarDays(createdDate, asOfDate));
      } else {
        const createdDate = parseBackendDateToLocal(destuffDateStr);
        const asOfDate =
          terminalType === 4 ? new Date() : effectiveChargesDateForCalculations ?? new Date();
        if (createdDate) storageDays = Math.max(0, diffCalendarDays(createdDate, asOfDate));
      }
    }
    setStorageDays(storageDays);
    formik.setFieldValue("containerNumber", row?.containerNumber);
    formik.setFieldValue("gatePassId", row?.gateEntryId);
    setContainerDetails(row || {});
  };
  const renderContainerNumberField = () => (
    <Grid item xs={12} md={6}>
      <Box>
        <CommonAutoCompleteField
          required={!formik.values.isOpenTruck}
          cssClassName="customTextField"
          label="Container Number"
          placeholder="Select Container Number"
          name="containerNumber"
          optionsType={virContainerList}
          disabled={terminalType !== 4}
          getOptionLabel={(option) => option.containerNumber?.toString()}
          onChange={(row: any) => {
            formik.setFieldValue("containerNumber", row?.containerNumber || "");
            const selectedGatePassId = row?.gatePassId ?? row?.gateEntryId ?? "";
            formik.setFieldValue("gatePassId", selectedGatePassId);
            //  Auto-populate VUR and Vehicle numbers
            formik.setFieldValue("virNumber", row?.virNo || "");
            formik.setFieldValue("vehicleNumber", row?.vehicleRegNumber || "");
            const forwarderRaw = row?.forwarder || row?.forwarderId || row?.partyId || "";
            const partyName =
              row?.forwarderName ||
              row?.partyName ||
              resolveForwarderDisplayName(forwarderRaw) ||
              "";
            formik.setFieldValue("PartyId", partyName);
            getNominationsByParty(forwarderRaw || partyName);
            formik.setFieldValue("importer", row?.importer || "");
            formik.setFieldValue("indexNumber", "");
            formik.setFieldValue("indexWeight", "");

            if (selectedGatePassId) {
              getContainerIndexesByGatePassId(selectedGatePassId);
            } else {
              setContainerIndexesByGatePass([]);
            }

            setContainerDetails(row || {});
            if (row && row.containerNumber != "" && terminalType !== 4) {
              formik.setFieldValue("containerNumber", row?.containerNumber);
              setContainerDetails([]);
              formik.setFieldValue("indexNumber", "");
            }
            if (terminalType !== 4) {
              checkStorageCharges(row);
            }
          }}
          value={
            virContainerList.find(
              (st: any) => st.containerNumber === formik.values.containerNumber
            ) || null
          }
          error={formik.touched.containerNumber && Boolean(formik.errors.containerNumber)}
          helperText={formik.touched.containerNumber && formik.errors.containerNumber}
        />
      </Box>
    </Grid>
  );
  const terminalDetailsData = {
    ...containerDetails,
    invoiceType: formik.values.invoiceType,
    // For bill-grouped invoices (BYD / Partywise), index is derived from selected container(s).
    indexNumber: formik.values.indexNumber,
    consigneeName: (() => {
      if (containerDetails?.consigneeName) return containerDetails.consigneeName;
      if (!isBillGroupedInvoice) return "";
      const normalize = (v: any) =>
        String(v ?? "")
          .trim()
          .toUpperCase();
      const bill = normalize(formik.values.billNumber || "");
      const selected = Array.isArray(formik.values.containerNumbers)
        ? formik.values.containerNumbers.map((x: any) => normalize(x)).filter(Boolean)
        : [];
      if (!bill) return "";

      const selectedSet = new Set(selected);
      const row =
        (virContainerList || []).find((r: any) => {
          if (normalize(r?.billNumber || "") !== bill) return false;
          if (selectedSet.size === 0) return true;
          const cn = normalize(r?.containerNumber || "");
          return cn && selectedSet.has(cn);
        }) || null;
      return String(row?.consigneeName || "").trim();
    })(),
    consigneeNtn: (() => {
      if (containerDetails?.consigneeNtn) return containerDetails.consigneeNtn;
      if (!isBillGroupedInvoice) return "";
      const normalize = (v: any) =>
        String(v ?? "")
          .trim()
          .toUpperCase();
      const bill = normalize(formik.values.billNumber || "");
      const selected = Array.isArray(formik.values.containerNumbers)
        ? formik.values.containerNumbers.map((x: any) => normalize(x)).filter(Boolean)
        : [];
      if (!bill) return "";

      const selectedSet = new Set(selected);
      const row =
        (virContainerList || []).find((r: any) => {
          if (normalize(r?.billNumber || "") !== bill) return false;
          if (selectedSet.size === 0) return true;
          const cn = normalize(r?.containerNumber || "");
          return cn && selectedSet.has(cn);
        }) || null;
      return String(row?.consigneeNtn || "").trim();
    })(),
    bydContainerCountLabel: (() => {
      if (!isBillGroupedInvoice) return "";
      const normalize = (v: any) =>
        String(v ?? "")
          .trim()
          .toUpperCase();
      const bill = normalize(formik.values.billNumber || "");
      const selected = Array.isArray(formik.values.containerNumbers)
        ? formik.values.containerNumbers.map((x: any) => normalize(x)).filter(Boolean)
        : [];
      if (!bill || selected.length === 0) return "";

      const selectedSet = new Set(selected);
      const counts = new Map<string, number>();
      for (const row of virContainerList || []) {
        if (normalize(row?.billNumber || "") !== bill) continue;
        const cn = normalize(row?.containerNumber || "");
        if (!cn || !selectedSet.has(cn)) continue;
        const sizeRaw =
          row?.containerSize ??
          row?.container_size ??
          row?.size ??
          row?.containerSz ??
          row?.container_size_name ??
          row?.containerType ??
          row?.container_type ??
          "";
        const size = String(sizeRaw || "").trim();
        if (!size) continue;
        counts.set(size, (counts.get(size) || 0) + 1);
      }

      if (counts.size === 0) {
        const fallbackSize = String(containerDetails?.containerSize || "").trim();
        if (fallbackSize) return `${fallbackSize}x${selected.length}`;
        return "";
      }
      return Array.from(counts.entries())
        .sort(([a], [b]) => String(a).localeCompare(String(b)))
        .map(([size, count]) => `${size}x${count}`)
        .join(", ");
    })(),
    bydCargoTypesLabel: (() => {
      if (!isBillGroupedInvoice) return "";
      const normalize = (v: any) =>
        String(v ?? "")
          .trim()
          .toUpperCase();
      const bill = normalize(formik.values.billNumber || "");
      const selected = Array.isArray(formik.values.containerNumbers)
        ? formik.values.containerNumbers.map((x: any) => normalize(x)).filter(Boolean)
        : [];
      if (!bill || selected.length === 0) return "";

      const selectedSet = new Set(selected);
      const set = new Set<string>();
      for (const r of virContainerList || []) {
        if (normalize(r?.billNumber || "") !== bill) continue;
        const cn = normalize(r?.containerNumber || "");
        if (!cn || !selectedSet.has(cn)) continue;
        const ct =
          r?.cargoType ??
          r?.cargoTypes ??
          r?.cargo_type ??
          r?.cargoTypeName ??
          r?.cargo_type_name ??
          "";
        const v = String(ct || "").trim();
        if (v) set.add(v);
      }
      return set.size ? Array.from(set).join(", ") : "";
    })(),
    forwarderDisplayName:
      resolveForwarderDisplayName(formik.values.PartyId) ||
      resolveForwarderDisplayName(containerDetails?.forwarder) ||
      containerDetails?.forwarderName ||
      containerDetails?.partyName ||
      "",
    partywiseStorageDays: isPartywiseInvoice ? partywiseStorageMetrics.baseStorageDays : undefined,
    partywiseBaseStorageDays: isPartywiseInvoice
      ? partywiseStorageMetrics.baseStorageDays
      : undefined,
    partywiseContainerCount: isPartywiseInvoice
      ? partywiseStorageMetrics.containerCount
      : undefined,
    partywiseSizeBreakdown: isPartywiseInvoice
      ? partywiseStorageMetrics.sizeBreakdownLabel
      : undefined,
    virNumber: String(
      formik.values.virNumber ||
        containerDetails?.virNo ||
        containerDetails?.virNumber ||
        containerDetails?.vir ||
        ""
    ).trim(),
    containerNumbersLabel: (() => {
      const fromArray = Array.isArray(formik.values.containerNumbers)
        ? formik.values.containerNumbers.map((x: any) => String(x || "").trim()).filter(Boolean)
        : [];
      if (fromArray.length) return fromArray.join(", ");
      const fromString = String(
        formik.values.containerNumber || containerDetails?.containerNumber || ""
      )
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      return fromString.length ? fromString.join(", ") : "";
    })(),
  };
  return (
    <div>
      {/* If prompt, when navigating to another page inside the app */}
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <TabHeader module={moduleName} heading={pageHeading} link={NodCommercialRoute.path} />
      <Backdrop className={classes.backdrop} open={loading || loader2}>
        <CommonLoader />
      </Backdrop>
      <Box m={2} />
      <Card>
        <CardContent className={classes.cardMain}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item md={9}>
              <Box mt={1} mb={2} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Typography className={classes.pageHeading}> {pageHeading} </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formik.values.isOpenTruck)}
                      onChange={formik.handleChange}
                      name="isOpenTruck"
                      color="primary"
                    />
                  }
                  label="Open Truck"
                />
                {terminalType !== 4 && (
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginLeft: "22rem",
                    }}
                  >
                    <Typography>Import</Typography>
                    <FormControlLabel
                      style={{ margin: "0px" }}
                      control={
                        <Switch
                          checked={isExportMode}
                          onChange={(e) => {
                            setIsExportMode(e.target.checked);

                            // Always reset shared form fields when toggling modes to avoid carry-over
                            setFilteredNominations([]);
                            setnominationDetails({});
                            setContainerDetails({});
                            setVehicleStatusData({});
                            formik.setFieldValue("PartyId", "");
                            formik.setFieldValue("nominationName", "");
                            formik.setFieldValue("paymentType", "");
                            formik.setFieldValue("paymentQuantity", 1);
                            formik.setFieldValue("otherPaymentName", "");
                            formik.setFieldValue("charges", "");
                            formik.setFieldValue("taxTypeId", "");
                            formik.setFieldValue("remarks", "");

                            if (e.target.checked) {
                              getVehicleGDs();
                              // Clear import-specific fields
                              setVirContainerList([]);
                              formik.setFieldValue("virNumber", "");
                              formik.setFieldValue("indexNumber", "");
                            } else {
                              // Reset export-specific data so old export info doesn't bleed into import
                              setGdList([]);
                              setGdVehiclesList([]);
                              setGdContainerList([]);
                              formik.setFieldValue("gdNumber", "");
                            }

                            // Clear container/vehicle selections for both modes
                            formik.setFieldValue("vehicleNumber", "");
                            formik.setFieldValue("containerNumber", "");
                            formik.setFieldValue("gatePassId", "");
                          }}
                          color="primary"
                        />
                      }
                      label=""
                    />
                    <Typography>Export</Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={3} direction="row">
              <Box textAlign="right" mt={1} mb={1}>
                {terminalType !== 4 && (
                  <CommonButton
                    cssClass="customSubmitBtn"
                    onClick={() => {
                      if (isFuturePaymentInvoice) {
                        setIsFuturePaymentInvoice(false);
                        setFuturePaymentInvoiceDate("");
                        setFuturePaymentVirList([]);
                        fetchPendingContainers();
                      } else {
                        setIsFuturePaymentInvoice(true);
                        setFuturePaymentVirList([]);
                      }
                    }}
                  >
                    {isFuturePaymentInvoice ? "Discard" : "Future Payment Invoice"}
                  </CommonButton>
                )}
                <CommonButton cssClass="customSubmitBtn" onClick={() => resetWholePage()}>
                  Reset
                </CommonButton>
                {isEditMode ? (
                  <CommonButton
                    cssClass="customSubmitBtn"
                    isGreen
                    onClick={() => {
                      setConfirmMode("update");
                      submitCompletePage();
                    }}
                  >
                    Update
                  </CommonButton>
                ) : (
                  !(
                    terminalType !== 4 &&
                    isFuturePaymentInvoice &&
                    Boolean(futurePaymentInvoiceDate)
                  ) && (
                    <CommonButton
                      cssClass="customSubmitBtn"
                      isGreen
                      onClick={() => {
                        setConfirmMode("save");
                        submitCompletePage();
                      }}
                    >
                      Save
                    </CommonButton>
                  )
                )}
              </Box>
            </Grid>
          </Grid>
          <Box style={{ marginBottom: "10px" }}>
            <Divider />
          </Box>

          <>
            <Grid spacing={2} container direction="row">
              <Grid item xs={12} md={6}>
                <Grid spacing={2} container direction="row">
                  {isExportMode ? (
                    <>
                      {/* GD Number - Dropdown */}
                      {/* <Grid item xs={12} md={6}>
                      <Box>
                        <CommonAutoCompleteField
                          required={isExportMode && !formik.values.isOpenTruck}
                          disabled={isVehicleAdd || formik.values.isOpenTruck}
                          cssClassName="customTextField"
                          label="Good's Declaration Number (GD#)"
                          placeholder="Select GD#"
                          name="gdNumber"
                          optionsType={gdList}
                          getOptionLabel={(option: any) => option.gdNo || ""}
                          onChange={(e: any) => {
                            const selectedGd = e?.gdNo || "";
                            formik.setFieldValue("gdNumber", selectedGd);
                            formik.setFieldValue("vehicleNumber", "");
                            formik.setFieldValue("containerNumber", "");
                            setGdContainerList([]);
                            if (selectedGd) {
                              getVehiclesByGD(selectedGd);
                            }
                          }}
                          value={
                            gdList.find((st: any) => st.gdNo == formik.values.gdNumber) || null
                          }
                          error={formik.touched.gdNumber && Boolean(formik.errors.gdNumber)}
                          helperText={formik.touched.gdNumber && formik.errors.gdNumber}
                        />
                      </Box>
                    </Grid> */}
                      {/* Container Number - Auto-populated from vehicle */}
                      <Grid item xs={12} md={6}>
                        <Box>
                          <CommonAutoCompleteField
                            required={!formik.values.isOpenTruck}
                            cssClassName="customTextField"
                            label="Container Number"
                            placeholder="Select Container Number"
                            name="containerNumber"
                            optionsType={gdContainerList}
                            disabled={terminalType !== 4 || !formik.values.vehicleNumber}
                            getOptionLabel={(option) => option.containerNumber?.toString()}
                            onChange={(row: any) => {
                              formik.setFieldValue("containerNumber", row?.containerNumber || "");
                              formik.setFieldValue("gatePassId", row?.gatePassId || "");
                              setContainerDetails(row || {});
                              if (row && row.containerNumber && terminalType !== 4) {
                                checkStorageCharges(row);
                              }
                            }}
                            value={
                              gdContainerList.find(
                                (st: any) => st.containerNumber === formik.values.containerNumber
                              ) || null
                            }
                            error={
                              formik.touched.containerNumber &&
                              Boolean(formik.errors.containerNumber)
                            }
                            helperText={
                              formik.touched.containerNumber && formik.errors.containerNumber
                            }
                          />
                        </Box>
                      </Grid>
                    </>
                  ) : terminalType === 4 ? (
                    <>
                      {/* Container Number First */}
                      {renderContainerNumberField()}
                      {/* VIR Number - Manual */}
                      <Grid item xs={12} md={6}>
                        <Box>
                          <TextField
                            // required={!formik.values.isOpenTruck}
                            // disabled={isVehicleAdd || formik.values.isOpenTruck}
                            disabled={terminalType === 4}
                            className="customTextField"
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="VIR Number"
                            placeholder="Enter VIR#"
                            name="virNumber"
                            InputLabelProps={{ shrink: true }}
                            value={formik.values.virNumber}
                            onChange={(e) => {
                              formik.setFieldValue("virNumber", e.target.value);
                            }}
                            // onChange={formik.handleChange}
                            // error={formik.touched.virNumber && Boolean(formik.errors.virNumber)}
                            // helperText={formik.touched.virNumber && formik.errors.virNumber}
                          />
                        </Box>
                      </Grid>
                    </>
                  ) : (
                    <>
                      {/* Invoice Type (before VIR number) */}
                      <Grid item xs={12} md={6}>
                        <Box>
                          <CommonAutoCompleteField
                            cssClassName="customTextField"
                            label="Invoice Type"
                            placeholder="Select Invoice Type"
                            name="invoiceType"
                            disableClearable
                            disabled={isEditMode}
                            optionsType={INVOICE_TYPE_OPTIONS}
                            getOptionLabel={(option: any) => option?.name ?? option?.id ?? ""}
                            value={
                              INVOICE_TYPE_OPTIONS.find(
                                (st: any) => st.id === formik.values.invoiceType
                              ) || null
                            }
                            onChange={(option: any) => {
                              const nextType = option?.id ?? "Normal";
                              if (nextType === formik.values.invoiceType) return;
                              resetWholePage({ retainToggle: true, invoiceType: nextType });
                              if (nextType === "BYD") {
                                dispatch(getAllPartName(true));
                              }
                            }}
                          />
                        </Box>
                      </Grid>

                      {/* VIR Number - Dropdown */}
                      <Grid item xs={12} md={6}>
                        <Box>
                          <CommonAutoCompleteField
                            required={!formik.values.isOpenTruck}
                            disabled={isVehicleAdd || formik.values.isOpenTruck}
                            cssClassName="customTextField"
                            label="VIR Number"
                            placeholder="Select VIR#"
                            name="virNumber"
                            optionsType={
                              isFuturePaymentInvoice ? futurePaymentVirList : pendingContainerList
                            }
                            getOptionLabel={(option: any) =>
                              isFuturePaymentInvoice ? option.virNo || "" : option.id || ""
                            }
                            onChange={(e: any) => {
                              if (!e) {
                                formik.setFieldValue("virNumber", "");
                                formik.setFieldValue("indexNumber", "");
                                formik.setFieldValue("billNumber", "");
                                formik.setFieldValue("containerNumbers", []);
                                formik.setFieldValue("gatePassId", "");
                                formik.setFieldValue("containerNumber", "");
                                if (!isBydInvoice) {
                                  formik.setFieldValue("PartyId", "");
                                  formik.setFieldValue("nominationName", "");
                                  setFilteredNominations([]);
                                  setnominationDetails({});
                                }
                                setContainerDetails([]);
                                setVirContainerList([]);
                                setPartywiseContainerMetaCache({});
                                return;
                              }
                              const selectedVir = isFuturePaymentInvoice ? e.virNo : e.id;
                              getContainersByVir(selectedVir);
                              formik.setFieldValue("virNumber", selectedVir);
                              setContainerDetails([]);
                              formik.setFieldValue("indexNumber", "");
                              formik.setFieldValue("gatePassId", "");
                              formik.setFieldValue("billNumber", "");
                              formik.setFieldValue("containerNumbers", []);
                              formik.setFieldValue(
                                "containerNumber",
                                isBillGroupedInvoice ? "" : e?.containerNumber
                              );
                              if (!isBydInvoice) {
                                formik.setFieldValue("PartyId", "");
                                formik.setFieldValue("nominationName", "");
                                setFilteredNominations([]);
                                setnominationDetails({});
                              }
                            }}
                            onKeyUp={
                              isFuturePaymentInvoice
                                ? (e: any) => {
                                    setVehicleNumberLength(e.target.value.length);
                                    if (e.target.value.length == 2 && vehicleNumberLength < 2) {
                                      searchVirNumber(e.target.value);
                                    }
                                  }
                                : undefined
                            }
                            onKeyDownCapture={
                              isFuturePaymentInvoice
                                ? (e: any) => {
                                    if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
                                    else formik.handleChange(e);
                                  }
                                : undefined
                            }
                            onPaste={
                              isFuturePaymentInvoice
                                ? (e: any) => {
                                    if (/[^A-Za-z0-9\d]/.test(e.clipboardData.getData("text"))) {
                                      e.preventDefault();
                                      return;
                                    }
                                    if (e.clipboardData.getData("text").length >= 2) {
                                      searchVirNumber(e.clipboardData.getData("text"));
                                    }
                                  }
                                : undefined
                            }
                            value={
                              isFuturePaymentInvoice
                                ? futurePaymentVirList.find(
                                    (st: any) => st.virNo === formik.values.virNumber
                                  ) || null
                                : pendingContainerList.find(
                                    (st: any) => st.id == formik.values.virNumber
                                  ) || null
                            }
                            error={formik.touched.virNumber && Boolean(formik.errors.virNumber)}
                            helperText={formik.touched.virNumber && formik.errors.virNumber}
                          />
                        </Box>
                      </Grid>

                      {!isBillGroupedInvoice ? (
                        <>
                          {/* Index Number - Dropdown (Normal) */}
                          <Grid item xs={12} md={6}>
                            <Box>
                              <CommonAutoCompleteField
                                required={!formik.values.isOpenTruck}
                                disabled={isVehicleAdd || formik.values.isOpenTruck}
                                cssClassName="customTextField"
                                label="Index Number (Container Number)"
                                name="indexNumber"
                                placeholder="Select Index Number"
                                optionsType={virContainerList}
                                getOptionLabel={(option: any) => {
                                  const idx = option?.indexNumber ?? "";
                                  const cn = option?.containerNumber ?? "";
                                  return cn ? `${idx},  (${cn})` : `${idx}`;
                                }}
                                onChange={(row: any) => {
                                  if (!row) {
                                    formik.setFieldValue("indexNumber", "");
                                    formik.setFieldValue("gatePassId", "");
                                    formik.setFieldValue("containerNumber", "");
                                    formik.setFieldValue("billNumber", "");
                                    setContainerDetails([]);
                                    return;
                                  }
                                  if (
                                    shouldRequireDestuffing(formik.values.invoiceType) &&
                                    isDestuffingPending(row)
                                  ) {
                                    notifyDestuffingRequired();
                                    return;
                                  }
                                  formik.setFieldValue("indexNumber", row.indexNumber);
                                  formik.setFieldValue("gatePassId", row.gatePassId);
                                  const billFromRow = resolveManifestBillNo(
                                    row.indexNumber,
                                    row.gatePassId,
                                    row
                                  );
                                  formik.setFieldValue("billNumber", billFromRow);
                                  getContailerDetails(row.indexNumber, row.gatePassId, row);
                                  formik.setFieldValue("containerNumber", row.containerNumber);
                                }}
                                value={
                                  virContainerList.find(
                                    (st: any) => st.indexNumber == formik.values.indexNumber
                                  ) || null
                                }
                                error={
                                  formik.touched.indexNumber && Boolean(formik.errors.indexNumber)
                                }
                                helperText={formik.touched.indexNumber && formik.errors.indexNumber}
                              />
                            </Box>
                          </Grid>
                        </>
                      ) : (
                        <>
                          {/* Bill Number - Dropdown (BYD) */}
                          <Grid item xs={12} md={6}>
                            <Box>
                              <CommonAutoCompleteField
                                required={!formik.values.isOpenTruck}
                                disabled={isVehicleAdd || formik.values.isOpenTruck}
                                cssClassName="customTextField"
                                label="Bill Number"
                                name="billNumber"
                                placeholder="Select Bill Number"
                                optionsType={billNumberOptions}
                                getOptionLabel={(option: any) =>
                                  option?.billNumber?.toString() || ""
                                }
                                onChange={(row: any) => {
                                  if (!row) {
                                    formik.setFieldValue("billNumber", "");
                                    formik.setFieldValue("containerNumbers", []);
                                    formik.setFieldValue("indexNumber", "");
                                    formik.setFieldValue("gatePassId", "");
                                    formik.setFieldValue("containerNumber", "");
                                    formik.setFieldValue("vehicleNumber", "");
                                    setContainerDetails([]);
                                    return;
                                  }
                                  formik.setFieldValue("billNumber", row.billNumber);
                                  formik.setFieldValue("containerNumbers", []);
                                  formik.setFieldValue("indexNumber", "");
                                  formik.setFieldValue("gatePassId", row?.gatePassId ?? "");
                                  formik.setFieldValue("containerNumber", "");
                                  formik.setFieldValue("vehicleNumber", "");
                                  setContainerDetails([]);
                                }}
                                value={
                                  billNumberOptions.find(
                                    (st: any) => st.billNumber == formik.values.billNumber
                                  ) || null
                                }
                                error={
                                  formik.touched.billNumber && Boolean(formik.errors.billNumber)
                                }
                                helperText={formik.touched.billNumber && formik.errors.billNumber}
                              />
                            </Box>
                          </Grid>
                        </>
                      )}
                      {/* Container Number Last */}
                      {isBillGroupedInvoice
                        ? renderBydContainerNumberMultiSelect()
                        : renderContainerNumberField()}
                    </>
                  )}

                  {terminalType === 4 && (
                    <Grid item xs={12} md={6}>
                      {isExportMode ? (
                        <Box>
                          <CommonAutoCompleteField
                            cssClassName="customTextField"
                            label="Vehicle Number"
                            placeholder="Enter Vehicle Number"
                            name="vehicleNumber"
                            optionsType={vehicleRegNumberArr}
                            getOptionLabel={(option) => option.registrationNumber || ""}
                            required={formik.values.isOpenTruck}
                            value={
                              vehicleRegNumberArr.find(
                                (st: any) => st.registrationNumber == formik.values.vehicleNumber
                              ) || null
                            }
                            onChange={(option: any) => {
                              // your existing logic
                              formik.setFieldValue("vehicleNumber", option.registrationNumber);
                              formik.setFieldValue("gatePassId", option?.gatePassId);

                              // NEW — fetch vehicle status here
                              if (option?.vehicleId) {
                                getVehicleStatusByVehicleId(option.vehicleId);
                              }
                            }}
                            onKeyUp={(e: any) => {
                              setVehicleNumberLength(e.target.value.length);
                              if (e.target.value.length == 2 && vehicleNumberLength < 2) {
                                getVehicleByRegistrationNo(e.target.value);
                                return;
                              }
                            }}
                            error={
                              formik.touched.vehicleNumber && Boolean(formik.errors.vehicleNumber)
                            }
                            helperText={formik.touched.vehicleNumber && formik.errors.vehicleNumber}
                          />
                        </Box>
                      ) : (
                        <>
                          <Box style={{ display: formik.values.isOpenTruck ? "none" : "block" }}>
                            <TextField
                              className="customTextField"
                              size="small"
                              fullWidth
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              label="Vehicle Number"
                              name="vehicleNumber"
                              placeholder="Vehicle Number"
                              disabled={
                                terminalType === 4 ||
                                (isBillGroupedInvoice &&
                                  Array.isArray(formik.values.containerNumbers) &&
                                  formik.values.containerNumbers.length > 0)
                              }
                              value={formik.values.vehicleNumber?.toUpperCase()}
                              onChange={(e: any) => {
                                if (/[^A-Za-z0-9\d]/.test(e.nativeEvent.data)) e.preventDefault();
                                else handleChange(e);
                              }}
                              onPaste={(e: any) => {
                                if (/[^A-Za-z0-9\d]/.test(e.clipboardData.getData("text")))
                                  e.preventDefault();
                              }}
                              error={
                                formik.touched.vehicleNumber && Boolean(formik.errors.vehicleNumber)
                              }
                              helperText={
                                formik.touched.vehicleNumber && formik.errors.vehicleNumber
                              }
                            />
                          </Box>
                          <Box style={{ display: formik.values.isOpenTruck ? "block" : "none" }}>
                            <CommonAutoCompleteField
                              cssClassName="customTextField"
                              label="Vehicle Number"
                              placeholder="Enter Vehicle Number"
                              name="vehicleNumber"
                              optionsType={vehicleRegNumberArr}
                              disabled={
                                isBillGroupedInvoice &&
                                Array.isArray(formik.values.containerNumbers) &&
                                formik.values.containerNumbers.length > 0
                              }
                              getOptionLabel={(option) => option.registrationNumber || ""}
                              required={formik.values.isOpenTruck}
                              value={
                                vehicleRegNumberArr.find(
                                  (st: any) => st.registrationNumber == formik.values.vehicleNumber
                                ) || null
                              }
                              onChange={(option: any) => {
                                formik.setFieldValue("vehicleNumber", option.registrationNumber);
                                formik.setFieldValue("gatePassId", option?.gatePassId);
                                // Fetch vehicle status for inspect mode (non-export)
                                if (option?.vehicleId && !isExportMode) {
                                  getVehicleStatusByVehicleId(option.vehicleId);
                                }
                              }}
                              onKeyUp={(e: any) => {
                                setVehicleNumberLength(e.target.value.length);
                                if (e.target.value.length == 2 && vehicleNumberLength < 2) {
                                  getVehicleByRegistrationNo(e.target.value);
                                  return;
                                }
                              }}
                              // onKeyDownCapture={(e: any) => {
                              //   if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
                              //   else handleChange(e);
                              // }}
                              error={
                                formik.touched.vehicleNumber && Boolean(formik.errors.vehicleNumber)
                              }
                              helperText={
                                formik.touched.vehicleNumber && formik.errors.vehicleNumber
                              }
                            />
                          </Box>
                        </>
                      )}
                    </Grid>
                  )}
                  {!isExportMode && terminalType === 4 && (
                    <Grid item xs={12} md={6}>
                      <Box>
                        <TextField
                          className="customTextField"
                          fullWidth
                          disabled={true}
                          size="small"
                          variant="outlined"
                          label="Forwarder Name"
                          placeholder="Enter Forwarder Name"
                          name="PartyId"
                          InputLabelProps={{ shrink: true }}
                          value={formik.values.PartyId}
                          onChange={(e) => {
                            formik.setFieldValue("PartyId", e.target.value);
                          }}
                          error={formik.touched.PartyId && Boolean(formik.errors.PartyId)}
                          helperText={formik.touched.PartyId && formik.errors.PartyId}
                        />
                      </Box>
                    </Grid>
                  )}
                  {terminalType === 4 && (
                    <Grid item xs={12} md={6}>
                      <Box>
                        <TextField
                          className="customTextField"
                          placeholder="Select Importer Name"
                          fullWidth
                          disabled={true}
                          size="small"
                          variant="outlined"
                          label="Importer Name"
                          name="importer"
                          InputLabelProps={{ shrink: true }}
                          value={formik.values.importer}
                          onChange={(e) => {
                            formik.setFieldValue("importer", e.target.value);
                          }}
                          error={formik.touched.importer && Boolean(formik.errors.importer)}
                          helperText={formik.touched.importer && formik.errors.importer}
                        />
                      </Box>
                    </Grid>
                  )}
                  {terminalType === 4 && (
                    <Grid item xs={12} md={6}>
                      <Box>
                        <CommonAutoCompleteField
                          cssClassName="customTextField"
                          label="Index Number"
                          placeholder="Select Index Number"
                          name="indexNumber"
                          optionsType={containerIndexesByGatePass}
                          getOptionLabel={(option: any) => option?.indexNo?.toString() || ""}
                          value={
                            containerIndexesByGatePass.find(
                              (x: any) => String(x?.indexNo) === String(formik.values.indexNumber)
                            ) || null
                          }
                          onChange={(option: any) => {
                            if (!option) {
                              formik.setFieldValue("indexNumber", "");
                              formik.setFieldValue("indexWeight", "");
                              // importer can still come from container; keep it unless explicitly cleared
                              return;
                            }
                            formik.setFieldValue("indexNumber", option?.indexNo ?? "");
                            formik.setFieldValue("indexWeight", option?.indexWeight ?? "");
                            if (option?.importerName != null) {
                              formik.setFieldValue("importer", option.importerName);
                            }
                          }}
                          error={formik.touched.indexNumber && Boolean(formik.errors.indexNumber)}
                          helperText={
                            formik.touched.indexNumber && (formik.errors as any).indexNumber
                          }
                        />
                      </Box>
                    </Grid>
                  )}
                  {terminalType === 4 && (
                    <Grid item xs={12} md={6}>
                      <Box>
                        <TextField
                          className="customTextField"
                          placeholder="Index Weight"
                          fullWidth
                          disabled={true}
                          size="small"
                          variant="outlined"
                          label="Index Weight"
                          name="indexWeight"
                          InputLabelProps={{ shrink: true }}
                          value={formik.values.indexWeight}
                          onChange={(e) => {
                            formik.setFieldValue("indexWeight", e.target.value);
                          }}
                        />
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        required
                        label="Agent Name"
                        placeholder="Select Agent"
                        name="agentName"
                        optionsType={allAgetns}
                        getOptionLabel={(option: any) => option.name}
                        value={
                          allAgetns.find((nom: any) => nom.id === formik.values.agentNameId) || null
                        }
                        onChange={(option: any) => {
                          formik.setFieldValue("agentNameId", option?.id || "");
                          formik.setFieldValue("challanNumber", option?.challanNumber || "");
                        }}
                        error={formik.touched.agentNameId && Boolean(formik.errors.agentNameId)}
                        helperText={formik.touched.agentNameId && formik.errors.agentNameId}
                      />
                    </Box>
                  </Grid>
                  {terminalType === 4 && (
                    <Grid item xs={12} md={6}>
                      <Box>
                        <TextField
                          className="customTextField"
                          size="small"
                          fullWidth
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Challan Number"
                          name="challanNumber"
                          placeholder="Challan Number"
                          disabled={true}
                          value={formik.values.challanNumber}
                          error={
                            formik.touched.challanNumber && Boolean(formik.errors.challanNumber)
                          }
                          helperText={formik.touched.challanNumber && formik.errors.challanNumber}
                        />
                      </Box>
                    </Grid>
                  )}
                  {terminalType !== 4 && !isExportMode && isFuturePaymentInvoice && (
                    <Grid item xs={12} md={6}>
                      <Box>
                        <TextField
                          className="customTextField"
                          size="small"
                          fullWidth
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          label="Future Date"
                          name="futurePaymentInvoiceDate"
                          type="date"
                          value={futurePaymentInvoiceDate}
                          inputProps={{ min: getTodayYmd() }}
                          onChange={(e) => {
                            setFuturePaymentInvoiceDate(e.target.value);
                          }}
                        />
                      </Box>
                    </Grid>
                  )}

                  {terminalType !== 4 && (
                    <Grid item xs={12} md={6}>
                      <CommonTextFieldDate
                        cssClassName="customTextField"
                        label={"Invoice Date and Time"}
                        name={"printDate"}
                        required={true}
                        value={formik.values.printDate}
                        onChange={(e: any) => {
                          formik.setFieldValue("printDate", e);
                          setInvoiceDateTime(e);
                        }}
                        maxDate={getNowYmdHm()}
                        error={formik.touched.printDate && Boolean(formik.errors.printDate)}
                        helperText={formik.touched.printDate && (formik.errors.printDate as any)}
                      />
                    </Grid>
                  )}
                  {terminalType === 4 && (
                    <Grid xs={12} md={6}>
                      <Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(atCommercialStation)}
                              onChange={(event) => {
                                setatCommercialStation(event.target.checked);
                              }}
                              name="atCommercialStation"
                              color="primary"
                            />
                          }
                          label="Vehicle at Commercial Station"
                        />
                      </Box>
                    </Grid>
                  )}
                  {terminalType !== 4 && (
                    <Grid item xs={12} md={6} spacing={2}>
                      <Box
                        display="flex"
                        justifyContent="space-between" // spreads buttons evenly across available width
                        alignItems="center"
                        flexWrap="wrap"
                        style={{ gap: 1.5, marginTop: 1 }}
                      >
                        {!isExportMode && (!isBillGroupedInvoice || isPartywiseInvoice) && (
                          <CommonButton
                            disable={
                              hasTariffPaymentInGrid() ||
                              (isPartywiseInvoice
                                ? formik.values.virNumber === "" ||
                                  formik.values.billNumber === "" ||
                                  !Array.isArray(formik.values.containerNumbers) ||
                                  formik.values.containerNumbers.length === 0 ||
                                  !hasInvoiceManifestDetails
                                : formik.values.virNumber === "" ||
                                  formik.values.indexNumber === "" ||
                                  !hasInvoiceManifestDetails)
                            }
                            cssClass="customSubmitBtn"
                            onClick={() => {
                              calculateTariffPayment(formik.values, formik);
                            }}
                          >
                            Tarrif Payment
                          </CommonButton>
                        )}

                        {!isExportMode && isBydInvoice && (
                          <CommonButton
                            disable={
                              formik.values.virNumber === "" ||
                              formik.values.billNumber === "" ||
                              !Array.isArray(formik.values.containerNumbers) ||
                              formik.values.containerNumbers.length === 0 ||
                              !hasInvoiceManifestDetails
                            }
                            cssClass="customSubmitBtn"
                            onClick={() => {
                              calculateBydCharges(formik.values);
                            }}
                          >
                            BYD Charges
                          </CommonButton>
                        )}

                        {(!isBillGroupedInvoice || isPartywiseInvoice) && (
                          <CommonButton
                            disable={
                              isPartywiseInvoice
                                ? formik.values.virNumber === "" ||
                                  formik.values.billNumber === "" ||
                                  !Array.isArray(formik.values.containerNumbers) ||
                                  formik.values.containerNumbers.length === 0
                                : isExportMode
                                ? formik.values.vehicleNumber === ""
                                : formik.values.virNumber === "" || formik.values.indexNumber === ""
                            }
                            cssClass="customSubmitBtn"
                            onClick={() => {
                              calculateStoragePayment(formik.values, formik);
                            }}
                          >
                            Storage Charges
                          </CommonButton>
                        )}
                        {!isExportMode && !isBillGroupedInvoice && (
                          <CommonButton
                            disable={
                              containerDetails?.cargoType !== "DG" ||
                              (isExportMode
                                ? formik.values.gdNumber == "" || formik.values.vehicleNumber == ""
                                : formik.values.virNumber == "" || formik.values.indexNumber == "")
                            }
                            cssClass="customSubmitBtn"
                            onClick={() => {
                              calculateDGCargoPayment(formik.values, formik);
                            }}
                          >
                            DG Cargo Charges
                          </CommonButton>
                        )}
                        {/* Examination Charges - Export only */}
                        {isExportMode && (
                          <CommonButton
                            disable={formik.values.vehicleNumber === ""}
                            cssClass="customSubmitBtn"
                            onClick={() => {
                              calculateExaminationCharges(formik.values, formik);
                            }}
                          >
                            Examination Charges
                          </CommonButton>
                        )}
                      </Box>
                    </Grid>
                  )}
                </Grid>
                <Grid spacing={2} container direction="row">
                  <Grid item xs={12} md={6}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        error={formik.touched.paymentType && Boolean(formik.errors.paymentType)}
                        helperText={formik.touched.paymentType && formik.errors.paymentType}
                        label="Type of Payment"
                        placeholder="Type of Payment"
                        name="paymentType"
                        optionsType={paymentTypesList}
                        getOptionLabel={(option) => option.name}
                        groupBy={(option) => option.isFixed}
                        renderGroup={(params) => (
                          <li key={params.key}>
                            <GroupHeader>
                              {Number(params.group) == 0 ? "Variable Charges" : "Fixed Charges"}
                            </GroupHeader>
                            <GroupItems>{params.children}</GroupItems>
                          </li>
                        )}
                        required
                        value={
                          paymentTypesList.find(
                            (st: any) => st.name === formik.values.paymentType
                          ) || null
                        }
                        onChange={(option: any) => {
                          if (option && option.id !== 0) {
                            setHasUserSelectedPaymentType(true);
                            formik.setFieldValue("paymentTypeId", option.id);
                            formik.setFieldValue("paymentType", option.name);
                            formik.setFieldValue("isFixed", option.isFixed);
                            handleChange({ target: { value: option.name } });
                            if (option.name === "Other") {
                              setIsOtherPayment(true);
                            } else {
                              dispatch(getPaymentTypeDetailsAction(option.id));
                              setIsOtherPayment(false);
                            }
                          } else {
                            setHasUserSelectedPaymentType(false);
                            handleChange({ target: { value: null } });
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    className={!isOtherPayment ? classes.hideDatesGrid : ""}
                  >
                    <TextField
                      className="customTextField"
                      required
                      size="small"
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Other Payment"
                      name="otherPaymentName"
                      placeholder="Other Payment"
                      disabled={false}
                      value={formik.values.otherPaymentName}
                      onChange={handleChange}
                      error={
                        formik.touched.otherPaymentName && Boolean(formik.errors.otherPaymentName)
                      }
                      helperText={formik.touched.otherPaymentName && formik.errors.otherPaymentName}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <TextField
                        className="customTextField"
                        required
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Quantity"
                        name="paymentQuantity"
                        placeholder="Quantity"
                        disabled={
                          paymentTypeDetail.name === "Tarrif Payment" ||
                          paymentTypeDetail.name === "Port Demurrage Charges" ||
                          paymentTypeDetail.name === "Detention Charges" ||
                          (terminalType === 4 &&
                            paymentTypeDetail.name === "Storage Payment Per Ton/Day") ||
                          isPartywiseInvoice
                        }
                        onKeyDown={(evt) =>
                          (evt.key === "e" || evt.key === ".") && evt.preventDefault()
                        }
                        value={formik.values.paymentQuantity}
                        onChange={handleChange}
                        onPaste={(e: any) => {
                          if (/[^0-9]/.test(e.clipboardData.getData("text"))) e.preventDefault();
                        }}
                        error={
                          formik.touched.paymentQuantity && Boolean(formik.errors.paymentQuantity)
                        }
                        helperText={formik.touched.paymentQuantity && formik.errors.paymentQuantity}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <TextField
                        className="customTextField"
                        required
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        onKeyDown={(evt) =>
                          (evt.key === "e" || evt.key === ".") && evt.preventDefault()
                        }
                        label="Charges"
                        name="charges"
                        placeholder="Charges"
                        // placeholder="Charges Per Quantity"
                        disabled={
                          paymentTypeDetail.name === "Tarrif Payment" ||
                          paymentTypeDetail.name === "Port Demurrage Charges" ||
                          paymentTypeDetail.name === "Detention Charges" ||
                          (terminalType === 4 &&
                            paymentTypeDetail.name === "Storage Payment Per Ton/Day") ||
                          (terminalType === 4 && discountApplied && discountPercentage > 0)
                        }
                        value={
                          terminalType === 4 &&
                          discountApplied &&
                          discountPercentage > 0 &&
                          originalCharges > 0
                            ? Math.round(
                                originalCharges - (originalCharges * discountPercentage) / 100
                              )
                            : originalCharges > 0
                            ? originalCharges
                            : formik.values.charges
                        }
                        onChange={(e) => {
                          const value = Number(e.target.value) || 0;
                          // If discount is applied, we need to calculate backwards to get original charges
                          if (terminalType === 4 && discountApplied && discountPercentage > 0) {
                            // Calculate original from discounted amount: discounted = original * (1 - percentage/100)
                            // So: original = discounted / (1 - percentage/100)
                            const calculatedOriginal = value / (1 - discountPercentage / 100);
                            setOriginalCharges(calculatedOriginal);
                            formik.setFieldValue("charges", value);
                          } else {
                            setOriginalCharges(value);
                            handleChange(e);
                          }
                        }}
                        onPaste={(e: any) => {
                          if (/[^0-9]/.test(e.clipboardData.getData("text"))) e.preventDefault();
                        }}
                        error={formik.touched.charges && Boolean(formik.errors.charges)}
                        helperText={formik.touched.charges && formik.errors.charges}
                      />
                    </Box>
                  </Grid>
                  {terminalType === 4 && formik.values.paymentType && (
                    <>
                      <Grid item xs={12} md={6}>
                        <Box>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={discountApplied}
                                onChange={(event) => {
                                  setDiscountApplied(event.target.checked);
                                  if (!event.target.checked) {
                                    setDiscountPercentage(0);
                                    // Restore original charges when discount is unchecked
                                    if (originalCharges > 0) {
                                      formik.setFieldValue("charges", originalCharges);
                                    }
                                  } else {
                                    // Apply discount when checked
                                    if (originalCharges > 0 && discountPercentage > 0) {
                                      const discountedCharges = Math.round(
                                        originalCharges -
                                          (originalCharges * discountPercentage) / 100
                                      );
                                      formik.setFieldValue("charges", discountedCharges);
                                    }
                                  }
                                }}
                                name="discountApplied"
                                color="primary"
                              />
                            }
                            label="Discount Applied"
                          />
                        </Box>
                      </Grid>
                      {discountApplied && (
                        <Grid item xs={12} md={6}>
                          <Box>
                            <TextField
                              className="customTextField"
                              type="number"
                              InputProps={{ inputProps: { min: 0, max: 100 } }}
                              size="small"
                              fullWidth
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              onKeyDown={(evt) =>
                                (evt.key === "e" ||
                                  evt.key === "." ||
                                  evt.key === "-" ||
                                  evt.key === "+") &&
                                evt.preventDefault()
                              }
                              label="Discount Percentage"
                              name="discountPercentage"
                              placeholder="Enter Discount Percentage"
                              disabled={false}
                              value={discountPercentage}
                              onChange={(e) => {
                                const value = Number(e.target.value) || 0;
                                const percentage = value > 100 ? 100 : value;
                                setDiscountPercentage(percentage);
                                // Update charges field when discount percentage changes
                                if (originalCharges > 0) {
                                  const discountedCharges = Math.round(
                                    originalCharges - (originalCharges * percentage) / 100
                                  );
                                  formik.setFieldValue("charges", discountedCharges);
                                }
                              }}
                              onPaste={(e: any) => {
                                if (/[^0-9]/.test(e.clipboardData.getData("text")))
                                  e.preventDefault();
                              }}
                              error={discountPercentage > 100}
                              helperText={
                                discountPercentage > 100 ? "Percentage cannot exceed 100" : ""
                              }
                            />
                          </Box>
                        </Grid>
                      )}
                      {discountApplied && discountPercentage > 0 && originalCharges > 0 && (
                        <Grid item xs={12} md={6}>
                          <Box>
                            <TextField
                              className="customTextField"
                              type="number"
                              InputProps={{ inputProps: { min: 0 } }}
                              size="small"
                              fullWidth
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              label="Total Charges"
                              name="discountedCharges"
                              placeholder="Total Charges"
                              disabled={true}
                              value={originalCharges > 0 ? Math.round(originalCharges) : 0}
                              error={false}
                              helperText="Amount without discount"
                            />
                          </Box>
                        </Grid>
                      )}
                    </>
                  )}
                  <Grid item xs={12} md={6}>
                    <Box>
                      <TextField
                        className="customTextField"
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Currency"
                        name="currency"
                        disabled={true}
                        value={formik.values.currency}
                        onChange={handleChange}
                        error={formik.touched.currency && Boolean(formik.errors.currency)}
                        helperText={formik.touched.currency && formik.errors.currency}
                      />
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={!isOtherPayment ? 12 : 6}
                    md={!isOtherPayment ? 6 : 12}
                    lg={!isOtherPayment ? 12 : 6}
                    direction="row"
                  >
                    <Box
                      textAlign="inherit"
                      mt={1}
                      mb={1}
                      className="terminalChargesSearchBtns headerButtonsConfig"
                      style={{ float: "right" }}
                    >
                      <CommonButton
                        cssClass="customSubmitBtn"
                        onClick={() => {
                          resetFilterForm();
                        }}
                      >
                        Reset
                      </CommonButton>
                      <CommonButton
                        cssClass="customSubmitBtn"
                        isGreen
                        onClick={() => formik.submitForm()}
                      >
                        {isEditMode && editingGridItemId ? "Update" : "Add"}
                      </CommonButton>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <TerminalDetails
                  details={terminalDetailsData}
                  nomination={nominationDetails}
                  vehicleStatus={vehicleStatusData}
                  isExportMode={isExportMode}
                  effectiveDate={effectiveChargesDateForCalculations}
                  agent={((allAgetns as any[]) || []).find(
                    (a: any) => Number(a?.id) === Number(formik.values.agentNameId)
                  )}
                />
              </Grid>
            </Grid>

            <div className={classes.grid}>
              <CommonMaterialGrid
                rows={filterGridRecord}
                columns={displayColumns}
                defaultPerPageRecord={50}
                height={450}
                isExport={isExport}
                exportHandler={exportHandler}
                filename={`Commercial_Terminal_charges_${Date.now()}`}
                onRowClick={isEditMode ? handleGridRowClick : undefined}
                getRowHighlightId={editingGridItemId}
              />
            </div>
            <Grid spacing={2} container direction="row">
              <Grid item xs={4} md={4} className={classes.remarksDiv}>
                <Box>
                  <TextField
                    className="customTextField"
                    size="small"
                    fullWidth
                    multiline
                    rows={1}
                    rowsMax={5}
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label="Remarks"
                    name="remarks"
                    placeholder="Remarks"
                    disabled={false}
                    value={formik.values.remarks}
                    onChange={handleChange}
                    error={formik.touched.remarks && Boolean(formik.errors.remarks)}
                    helperText={formik.touched.remarks && formik.errors.remarks}
                  />
                </Box>
              </Grid>
              <Grid item xs={4} md={4} className={classes.remarksDiv}>
                <Box>
                  <CommonAutoCompleteField
                    cssClassName="customTextField"
                    label="Tax Type (%)"
                    placeholder="Select Tax Type"
                    name="taxTypeId"
                    optionsType={allTaxTypeRows}
                    getOptionLabel={(option) =>
                      `${option.taxType.toString()} - ${option?.taxRate.toString()}%`
                    }
                    onChange={(row: any) => {
                      formik.setFieldValue("taxTypeId", row?.id);
                      formik.setFieldValue("tax", row);
                    }}
                    value={
                      allTaxTypeRows.find((st: any) => st.id == formik.values.taxTypeId) || null
                    }
                    error={formik.touched.taxTypeId && Boolean(formik.errors.taxTypeId)}
                    helperText={formik.touched.taxTypeId && formik.errors.taxTypeId}
                  />
                </Box>
              </Grid>
              <Grid item xs={4} md={4} className={classes.remarksDiv}>
                <Box>
                  <TextField
                    className="customTextField"
                    type="number"
                    InputProps={{ inputProps: { min: 0, max: Number(totalAmount) } }}
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onKeyDown={(evt) =>
                      (evt.key === "e" || evt.key === "." || evt.key === "-" || evt.key === "+") &&
                      evt.preventDefault()
                    }
                    label="Discount"
                    name="discount"
                    placeholder="Enter Discount"
                    disabled={false}
                    value={formik.values.discount}
                    onChange={handleChange}
                    onPaste={(e: any) => {
                      if (/[^0-9]/.test(e.clipboardData.getData("text"))) e.preventDefault();
                    }}
                    error={formik.touched.discount && Boolean(formik.errors.discount)}
                    helperText={formik.touched.discount && formik.errors.discount}
                  />
                </Box>
              </Grid>
            </Grid>

            <Grid
              item
              md={12}
              container
              className={`${classes.totalDiv} ${"commercialChargeTotalDiv"}`}
            >
              <Grid item md={3}>
                <Typography variant="h6" className={classes.subHeadings}>
                  Total <span style={{ fontSize: "10px" }}>({formik.values.currency})</span>:{"  "}
                  <span className={classes.totalVal}>{`${numberFormatting(
                    Number(totalAmount)
                  )} `}</span>
                </Typography>
              </Grid>
              {terminalType !== 4 && (
                <Grid item md={3}>
                  <Typography variant="h6" className={classes.subHeadings}>
                    STax Amount <span style={{ fontSize: "10px" }}>({formik.values.currency})</span>
                    :{"  "}
                    <span className={classes.totalVal}>{` ${numberFormatting(
                      calculateTax()
                    )} `}</span>
                  </Typography>
                </Grid>
              )}
              <Grid item md={3}>
                <Typography variant="h6" className={classes.subHeadings}>
                  Discount <span style={{ fontSize: "10px" }}>({formik.values.currency})</span>:
                  {"  "}
                  <span className={classes.totalVal}>{` ${numberFormatting(
                    Number(formik.values.discount) || 0
                  )} `}</span>
                </Typography>
              </Grid>
              <Grid item md={3}>
                <Typography variant="h6" className={classes.subHeadings}>
                  Net Amount <span style={{ fontSize: "10px" }}>({formik.values.currency})</span>:
                  {"  "}
                  <span className={classes.totalVal}>
                    {" "}
                    {` ${numberFormatting(calculateNetAmount())} `}
                  </span>
                </Typography>
              </Grid>
            </Grid>
          </>
        </CardContent>
      </Card>
      <TransitionDialog
        isOpen={isOpen}
        handleClose={handleCloseDialog}
        heading={"Are you sure you want to save the following charges?"}
        handleConfirm={() => {
          if (confirmMode === "update") {
            updatePaymentDetails();
          } else {
            handleConfirmDialog();
          }
        }}
      >
        {dialogContent &&
          dialogContent?.split("#").map((data: any, index: any) => (
            <Box key={index}>
              <p style={{ lineHeight: "0.7" }}>{data}</p>
            </Box>
          ))}
      </TransitionDialog>

      <TransitionDialog
        isOpen={isOpenDeleteDailog}
        handleClose={handleCloseDeleteDialog}
        heading={"Confirmation"}
        handleConfirm={handleDeleteDialog}
      >
        Are you sure you want to delete this record?
      </TransitionDialog>

      <TransitionDialog
        isOpen={isOpenDublicateDailog}
        handleClose={handleCloseDublicateDialog}
        heading={"Confirmation"}
        handleConfirm={handleSaveDublicateDialog}
      >
        Record against vehicle already present in same trip, do you want to save another one?
      </TransitionDialog>
    </div>
  );
}
